import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useFiscalYear } from '../context/FiscalYearContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { DateInput } from '../components/ui/date-input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '../components/ui/command';
import {
  ClipboardList, Plus, Edit, Trash2, Send, Eye, Printer,
  Search, Filter, Check, Package, Truck, FileText, Save,
  ArrowRight, AlertTriangle, ChevronDown, ChevronUp, Undo2, X, Loader2,
  BarChart3, FileDown, CheckCircle2, XCircle, MinusCircle, Calculator, ChevronsUpDown,
  TrendingUp, DollarSign, Clock, LayoutDashboard, CalendarClock, Sparkles
} from 'lucide-react';
import axios from 'axios';
import { formatUSD, formatDate, cn } from '../lib/utils';
import { toast } from 'sonner';
import InventorySelector from '../components/selectors/InventorySelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  draft: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  approved: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  submitted: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  sent: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  partial_received: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  delivered: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  received: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  posted: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
};

const statusLabels = {
  draft: 'Draft', approved: 'Approved', submitted: 'Submitted', sent: 'Sent',
  partial_received: 'Partial', delivered: 'Delivered', received: 'Received', posted: 'Posted'
};

const lineStatusColors = {
  FULL: 'bg-emerald-500/20 text-emerald-400',
  PARTIAL: 'bg-amber-500/20 text-amber-400',
  NOT_RECEIVED: 'bg-red-500/20 text-red-400',
};

// Searchable Supplier Select Component
const SearchableSupplierSelect = ({ suppliers, value, onValueChange, placeholder, disabled, testId }) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  const filteredSuppliers = suppliers.filter(s => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return s.code?.toLowerCase().includes(query) || 
           s.name?.toLowerCase().includes(query);
  });
  
  const selectedSupplier = suppliers.find(s => s.id === value);
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between font-normal h-9", disabled && "opacity-50 cursor-not-allowed")}
          disabled={disabled}
          data-testid={testId}
        >
          {value && selectedSupplier ? (
            <span className="truncate">{selectedSupplier.code} - {selectedSupplier.name}</span>
          ) : (
            <span className="text-muted-foreground">{placeholder || 'Select supplier...'}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[350px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput 
            placeholder="Search by code or name..." 
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            <CommandEmpty>No suppliers found.</CommandEmpty>
            <CommandGroup>
              {filteredSuppliers.slice(0, 50).map((supplier) => (
                <CommandItem
                  key={supplier.id}
                  value={supplier.id}
                  onSelect={() => {
                    onValueChange(supplier.id);
                    setOpen(false);
                    setSearchQuery('');
                  }}
                >
                  <Check className={cn("mr-2 h-4 w-4", value === supplier.id ? "opacity-100" : "opacity-0")} />
                  <span className="font-mono text-xs mr-2">{supplier.code}</span>
                  <span className="truncate">{supplier.name}</span>
                </CommandItem>
              ))}
              {filteredSuppliers.length > 50 && (
                <div className="px-2 py-1.5 text-xs text-muted-foreground text-center">
                  Type to search more...
                </div>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const PurchaseOrdersPage = () => {
  const { user, currentOrg, canEdit, isVatRegistered, token } = useAuth();
  const { selectedFY } = useFiscalYear();

  // Currency-aware formatting helper
  const formatCurrency = (value, currency) => {
    const num = Number(value) || 0;
    if (currency === 'EUR') {
      return `EUR ${num.toLocaleString()}`;
    }
    return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 3 })}`;
  };

  // View state
  const [activeTab, setActiveTab] = useState('dashboard'); // dashboard, list, analyze, form, receive

  // List state
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [viewOrder, setViewOrder] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPostDialog, setShowPostDialog] = useState(false);
  const [postingOrder, setPostingOrder] = useState(null);
  const [posting, setPosting] = useState(false);

  // Analysis state
  const [analysisFromDate, setAnalysisFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [analysisToDate, setAnalysisToDate] = useState(new Date().toISOString().split('T')[0]);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [selectedSuggestions, setSelectedSuggestions] = useState({});
  const [supplierFilter, setSupplierFilter] = useState(''); // Filter results by supplier

  // Goods Receipt state
  const [receivingOrder, setReceivingOrder] = useState(null);
  const [receivedQtys, setReceivedQtys] = useState({});
  const [receiveNotes, setReceiveNotes] = useState({});
  const [submittingReceipt, setSubmittingReceipt] = useState(false);

  // Supplier & Inventory data
  const [supplierAccounts, setSupplierAccounts] = useState([]);
  const [purchaseAccounts, setPurchaseAccounts] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [showLowStock, setShowLowStock] = useState(false);

  // Post dialog accounts
  const [postDebitId, setPostDebitId] = useState('');
  const [postCreditId, setPostCreditId] = useState('');

  // Dashboard state
  const [dashboardData, setDashboardData] = useState(null);
  const [loadingDashboard, setLoadingDashboard] = useState(false);

  // Supplier Analysis state
  const [saSupplier, setSaSupplier] = useState('');
  const [saFromDate, setSaFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [saToDate, setSaToDate] = useState(new Date().toISOString().split('T')[0]);
  const [saAnalyzing, setSaAnalyzing] = useState(false);
  const [saResult, setSaResult] = useState(null);
  const [saSelected, setSaSelected] = useState({});
  const [saAiLoading, setSaAiLoading] = useState(false);
  const [saAiInsights, setSaAiInsights] = useState(null);
  const [saAiLang, setSaAiLang] = useState('en');

  const [exchangeRate] = useState(0.0015244895);

  const emptyLine = {
    inventory_item_id: '', item_name: '', item_name_ar: '', barcode: '',
    quantity: 0, free_qty: 0, unit: 'piece', unit_price: 0, selling_price: 0,
    margin_percent: 0, currency: 'EUR',
    exchange_rate: 1, discount_percent: 0, line_total: 0, line_total_usd: 0,
    batch_number: '', expiry_date: '', notes: ''
  };

  const emptyOrder = {
    date: new Date().toISOString().split('T')[0],
    expected_delivery_date: '',
    order_type: 'supplier',
    supplier_id: '', supplier_name: '', supplier_code: '',
    lines: [{ ...emptyLine }],
    subtotal: 0, discount_percent: 0, discount_amount: 0,
    tax_percent: 0, tax_amount: 0, total: 0, total_usd: 0,
    currency: 'XOF', notes: ''
  };

  const [order, setOrder] = useState({ ...emptyOrder });

  // Fetch data on mount
  useEffect(() => {
    if (!currentOrg) return;
    fetchOrders();
    fetchReferenceData();
  }, [currentOrg, filterStatus, filterType]);

  // Enrich order with dual-currency totals from line items
  const enrichOrderTotals = useCallback((o) => {
    if (!o.lines || o.lines.length === 0) return o;
    const hasMixed = o.lines.some(l => (l.currency || o.currency) === 'XOF') && o.lines.some(l => (l.currency || o.currency) === 'EUR');
    let total_usd = 0, total_lbp = 0;
    for (const l of o.lines) {
      const qty = parseFloat(l.quantity) || 0;
      const price = parseFloat(l.unit_price) || 0;
      const disc = parseFloat(l.discount_percent) || 0;
      const lineTotal = qty * price * (1 - disc / 100);
      const curr = l.currency || o.currency || 'EUR';
      if (curr === 'EUR') {
        total_lbp += lineTotal;
        total_usd += exchangeRate > 0 ? lineTotal / exchangeRate : 0;
      } else {
        total_usd += lineTotal;
        total_lbp += lineTotal * exchangeRate;
      }
    }
    return { ...o, total_usd: Math.round(total_usd * 100) / 100, total_lbp: Math.round(total_lbp), subtotal_usd: Math.round(total_usd * 100) / 100, subtotal_lbp: Math.round(total_lbp), has_mixed_currency: hasMixed };
  }, [exchangeRate]);

  const fetchOrders = async () => {
    if (!currentOrg) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ organization_id: currentOrg.id });
      if (filterStatus !== 'all') params.set('status', filterStatus);
      if (filterType !== 'all') params.set('order_type', filterType);
      if (searchTerm) params.set('search', searchTerm);
      const res = await axios.get(`${API}/purchase-orders?${params}`);
      setOrders(res.data.map(enrichOrderTotals));
    } catch {
      toast.error('Failed to load purchase orders');
    }
    setLoading(false);
  };

  const fetchReferenceData = async () => {
    if (!currentOrg) return;
    try {
      const [suppRes, purchRes, invRes, lowRes] = await Promise.all([
        axios.get(`${API}/supplier-accounts?organization_id=${currentOrg.id}`).catch(() => ({ data: [] })),
        axios.get(`${API}/purchase-accounts?organization_id=${currentOrg.id}`).catch(() => ({ data: [] })),
        axios.get(`${API}/inventory?organization_id=${currentOrg.id}&page_size=500`).catch(() => ({ data: [] })),
        axios.get(`${API}/purchase-orders/low-stock-suggestions?organization_id=${currentOrg.id}`).catch(() => ({ data: [] }))
      ]);
      setSupplierAccounts(suppRes.data);
      setPurchaseAccounts(purchRes.data);
      setInventoryItems(Array.isArray(invRes.data) ? invRes.data : invRes.data?.items || []);
      setLowStockItems(lowRes.data);
    } catch { /* */ }
  };

  // Fetch Dashboard Data
  const fetchDashboard = async () => {
    if (!currentOrg) return;
    setLoadingDashboard(true);
    try {
      const res = await axios.get(`${API}/purchase-orders/dashboard?organization_id=${currentOrg.id}`);
      setDashboardData(res.data);
    } catch (err) {
      console.error('Dashboard fetch failed:', err);
      toast.error('Failed to load dashboard data');
    }
    setLoadingDashboard(false);
  };

  // Fetch dashboard when tab changes to dashboard
  useEffect(() => {
    if (activeTab === 'dashboard' && !dashboardData) {
      fetchDashboard();
    }
  }, [activeTab]);

  // Line item calculations
  const recalcLine = (line) => {
    const qty = parseFloat(line.quantity) || 0;
    const price = parseFloat(line.unit_price) || 0;
    const disc = parseFloat(line.discount_percent) || 0;
    const gross = qty * price;
    const discAmt = gross * disc / 100;
    const total = gross - discAmt;
    const rate = line.currency === 'EUR' ? (parseFloat(line.exchange_rate) || exchangeRate) : 1;
    const totalLbp = line.currency === 'EUR' ? total : total * exchangeRate;
    const totalUsd = line.currency === 'EUR' ? total / rate : total;
    return { ...line, line_total: total, line_total_usd: totalUsd, line_total_lbp: totalLbp };
  };

  const recalcOrder = useCallback((o) => {
    const lines = o.lines.map(recalcLine);
    // Calculate separate subtotals per currency
    const subtotal_usd = lines.reduce((s, l) => s + (l.line_total_usd || 0), 0);
    const subtotal_lbp = lines.reduce((s, l) => s + (l.line_total_lbp || 0), 0);
    // Check if order has mixed currencies
    const hasMixed = lines.some(l => l.currency === 'XOF') && lines.some(l => l.currency === 'EUR');
    // Primary currency from first line
    const currency = lines[0]?.currency || 'EUR';
    // Subtotal in primary currency (for discount/tax calc)
    const subtotal = currency === 'EUR' ? subtotal_lbp : subtotal_usd;
    const discAmt = subtotal * (parseFloat(o.discount_percent) || 0) / 100;
    const afterDisc = subtotal - discAmt;
    const taxAmt = afterDisc * (parseFloat(o.tax_percent) || 0) / 100;
    const total = afterDisc + taxAmt;
    return {
      ...o, lines, subtotal: round3(subtotal), subtotal_usd: round3(subtotal_usd), subtotal_lbp: round3(subtotal_lbp),
      discount_amount: round3(discAmt), tax_amount: round3(taxAmt), 
      total: round3(total), total_usd: round3(subtotal_usd), total_lbp: round3(subtotal_lbp),
      currency, has_mixed_currency: hasMixed
    };
  }, [exchangeRate]);

  const round3 = (v) => Math.round(v * 1000) / 1000;

  const updateLine = (idx, field, value) => {
    const lines = [...order.lines];
    lines[idx] = { ...lines[idx], [field]: value };
    setOrder(recalcOrder({ ...order, lines }));
  };

  const addLine = () => setOrder(prev => ({ ...prev, lines: [...prev.lines, { ...emptyLine }] }));

  const removeLine = (idx) => {
    if (order.lines.length <= 1) return;
    const lines = order.lines.filter((_, i) => i !== idx);
    setOrder(recalcOrder({ ...order, lines }));
  };

  const addLowStockItem = (item) => {
    const newLine = {
      ...emptyLine,
      inventory_item_id: item.id,
      item_name: item.name,
      item_name_ar: item.name_ar || '',
      barcode: item.barcode || '',
      unit: item.unit || 'piece',
      unit_price: item.cost || 0,
      selling_price: item.price || 0,
      currency: item.currency || 'EUR',
      // Use pharmacist_margin as discount if no discount_percent
      discount_percent: item.pharmacist_margin || item.discount_percent || 0,
      quantity: Math.max((item.reorder_level || 5) - (item.on_hand_qty || 0), 1)
    };
    // Ensure item is in inventoryItems for InventorySelector display
    setInventoryItems(prev => prev.find(i => i.id === item.id) ? prev : [...prev, item]);
    setOrder(prev => recalcOrder({ ...prev, lines: [...prev.lines.filter(l => l.item_name), newLine] }));
    toast.success(`Added ${item.name}`);
  };

  const selectInventoryItem = (idx, item) => {
    const lines = [...order.lines];
    lines[idx] = {
      ...lines[idx],
      inventory_item_id: item.id,
      item_name: item.name,
      item_name_ar: item.name_ar || '',
      barcode: item.barcode || '',
      unit: item.unit || 'piece',
      unit_price: item.cost || 0,
      selling_price: item.price || 0,
      margin_percent: item.pharmacist_margin || 0,
      currency: item.currency || 'EUR',
      exchange_rate: (item.currency === 'EUR') ? exchangeRate : 1,
      discount_percent: 0
    };
    setOrder(recalcOrder({ ...order, lines }));
  };

  // Fetch all items for the selected supplier and add them with qty 0
  const [fetchingItems, setFetchingItems] = useState(false);
  const fetchSupplierItems = async () => {
    if (!order.supplier_id) {
      toast.error('Please select a supplier first');
      return;
    }
    setFetchingItems(true);
    try {
      const supplierCode = order.supplier_code || supplierAccounts.find(s => s.id === order.supplier_id)?.code;
      if (!supplierCode) {
        toast.error('Supplier code not found');
        setFetchingItems(false);
        return;
      }
      const res = await axios.get(`${API}/inventory?organization_id=${currentOrg.id}&supplier_id=${supplierCode}&page=1&page_size=5000`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000
      });
      const items = res.data?.items || res.data || [];
      if (items.length === 0) {
        toast.info('No items found for this supplier');
        return;
      }
      const newLines = items.map(item => recalcLine({
        ...emptyLine,
        inventory_item_id: item.id,
        item_name: item.name,
        item_name_ar: item.name_ar || '',
        barcode: item.barcode || '',
        unit: item.unit || 'piece',
        unit_price: item.cost || 0,
        selling_price: item.price || 0,
        margin_percent: item.pharmacist_margin || 0,
        currency: item.currency || 'EUR',
        exchange_rate: (item.currency === 'EUR') ? exchangeRate : 1,
        quantity: 0
      }));
      // Merge fetched items into inventoryItems so InventorySelector can display names
      setInventoryItems(prev => {
        const existingIds = new Set(prev.map(i => i.id));
        const newItems = items.filter(i => !existingIds.has(i.id));
        return newItems.length > 0 ? [...prev, ...newItems] : prev;
      });
      setOrder(prev => recalcOrder({ ...prev, lines: newLines }));
      toast.success(`Loaded ${items.length} items from supplier`);
    } catch (err) {
      console.error('Fetch supplier items failed:', err?.response?.data || err.message || err);
      toast.error(err?.response?.data?.detail || err.message || 'Failed to fetch supplier items');
    } finally {
      setFetchingItems(false);
    }
  };

  const selectSupplier = (suppId) => {
    const supp = supplierAccounts.find(s => s.id === suppId);
    if (supp) {
      setOrder(prev => ({ ...prev, supplier_id: supp.id, supplier_name: supp.name, supplier_code: supp.code }));
    }
  };

  // ==================== ANALYSIS FUNCTIONS ====================
  const runAnalysis = async () => {
    if (!analysisFromDate || !analysisToDate) {
      toast.error('Select date range');
      return;
    }
    setAnalyzing(true);
    setAnalysisResult(null);
    setSupplierFilter(''); // Reset filter
    try {
      const res = await axios.post(`${API}/purchase-orders/analyze-sales`, {
        organization_id: currentOrg.id,
        from_date: analysisFromDate,
        to_date: analysisToDate
      });
      setAnalysisResult(res.data);
      // Initialize all items with qty 0 - user must manually set order quantity
      const selected = {};
      res.data.suggestions.forEach(s => {
        selected[s.item_id] = { ...s, selected: false, qty: 0 };
      });
      setSelectedSuggestions(selected);
      toast.success(`Found ${res.data.total_items_sold} sold items`);
    } catch (err) {
      console.error('Analysis error:', err.response?.data);
      toast.error(err.response?.data?.detail || 'Analysis failed');
    }
    setAnalyzing(false);
  };

  // Filter suggestions by supplier (client-side)
  const filteredSuggestions = useMemo(() => {
    if (!analysisResult?.suggestions) return [];
    if (!supplierFilter) return analysisResult.suggestions;
    if (supplierFilter === 'none') return analysisResult.suggestions.filter(s => !s.supplier_id);
    return analysisResult.suggestions.filter(s => s.supplier_id === supplierFilter);
  }, [analysisResult, supplierFilter]);

  // Get unique suppliers from results for filter dropdown
  const suppliersInResults = useMemo(() => {
    if (!analysisResult?.suggestions) return [];
    const map = new Map();
    analysisResult.suggestions.forEach(s => {
      if (s.supplier_id && s.supplier_name) {
        map.set(s.supplier_id, { id: s.supplier_id, code: s.supplier_code, name: s.supplier_name });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [analysisResult]);

  const toggleSuggestion = (itemId) => {
    setSelectedSuggestions(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], selected: !prev[itemId]?.selected }
    }));
  };

  const updateSuggestionQty = (itemId, qty) => {
    setSelectedSuggestions(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], qty: parseInt(qty) || 0 }
    }));
  };

  // Get IDs of items visible under current supplier filter
  const filteredItemIds = useMemo(() => {
    return new Set(filteredSuggestions.map(s => s.item_id));
  }, [filteredSuggestions]);

  const createPOFromSuggestions = () => {
    // Only include items that match the current supplier filter
    const items = Object.values(selectedSuggestions).filter(s => s.selected && s.qty > 0 && filteredItemIds.has(s.item_id));
    if (items.length === 0) {
      toast.error('Select at least one item with qty > 0');
      return;
    }

    // Group by supplier if filter is set, otherwise leave blank
    const supplier = (supplierFilter && supplierFilter !== 'none') ? supplierAccounts.find(s => s.id === supplierFilter) : null;
    const lines = items.map(item => ({
      ...emptyLine,
      inventory_item_id: item.item_id,
      item_name: item.item_name,
      item_name_ar: item.item_name_ar || '',
      barcode: item.barcode || '',
      unit: item.unit || 'PC',
      unit_price: item.unit_cost || 0,
      selling_price: item.selling_price || 0,
      currency: item.currency || 'EUR',
      discount_percent: item.discount_percent || 0,
      quantity: item.qty,
      qty_sold: item.qty_sold,
      qty_on_hand: item.qty_on_hand,
      qty_on_order: item.qty_on_order
    }));

    const newOrder = {
      ...emptyOrder,
      supplier_id: supplier?.id || '',
      supplier_name: supplier?.name || '',
      supplier_code: supplier?.code || '',
      lines,
      notes: `Generated from sales analysis (${analysisFromDate} to ${analysisToDate})`
    };

    setOrder(recalcOrder(newOrder));
    // Ensure all suggestion items are in inventoryItems for InventorySelector display
    setInventoryItems(prev => {
      const existingIds = new Set(prev.map(i => i.id));
      const newItems = items.filter(i => i.item_id && !existingIds.has(i.item_id)).map(i => ({
        id: i.item_id, name: i.item_name, name_ar: i.item_name_ar || '', barcode: i.barcode || '',
        cost: i.unit_cost || 0, price: i.selling_price || 0, unit: i.unit || 'piece'
      }));
      return newItems.length > 0 ? [...prev, ...newItems] : prev;
    });
    setActiveTab('list');
    setShowForm(true);
    toast.success(`Created PO draft with ${items.length} items${!supplier ? ' - Select a supplier' : ''}`);
  };

  // ==================== SUPPLIER ANALYSIS ====================
  const runSupplierAnalysis = async () => {
    if (!saSupplier) { toast.error('Select a supplier first'); return; }
    if (!saFromDate || !saToDate) { toast.error('Select date range'); return; }
    setSaAnalyzing(true);
    setSaResult(null);
    setSaAiInsights(null);
    try {
      const res = await axios.post(`${API}/purchase-orders/analyze-supplier`, {
        organization_id: currentOrg.id,
        supplier_id: saSupplier,
        from_date: saFromDate,
        to_date: saToDate
      });
      setSaResult(res.data);
      // Initialize all items with qty 0 - user must manually set order quantity
      const sel = {};
      res.data.items.forEach(item => {
        sel[item.item_id] = { ...item, selected: false, qty: 0 };
      });
      setSaSelected(sel);
      toast.success(`Found ${res.data.total_items} items from ${res.data.supplier_name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Supplier analysis failed');
    }
    setSaAnalyzing(false);
  };

  const fetchAiInsights = async (retryCount = 0) => {
    if (!saResult) return;
    setSaAiLoading(true);
    try {
      const topSellers = saResult.items.filter(i => i.qty_sold > 0).slice(0, 10).map(i => ({
        item_name: i.item_name, qty_sold: i.qty_sold, avg_daily_sales: i.avg_daily_sales, qty_on_hand: i.qty_on_hand
      }));
      const deadStock = saResult.items.filter(i => i.is_dead_stock).slice(0, 15).map(i => ({
        item_name: i.item_name, qty_on_hand: i.qty_on_hand, cost: i.unit_cost, currency: i.currency, stock_value: i.qty_on_hand * i.unit_cost
      }));
      const res = await axios.post(`${API}/purchase-orders/supplier-ai-insights`, {
        organization_id: currentOrg.id,
        supplier_name: saResult.supplier_name,
        supplier_performance: saResult.supplier_performance,
        items_summary: {
          total_items: saResult.total_items, items_with_sales: saResult.items_with_sales,
          items_no_sales: saResult.items_no_sales, dead_stock_count: saResult.dead_stock_count
        },
        dead_stock_items: deadStock,
        top_sellers: topSellers,
        days_in_period: saResult.days_in_period,
        from_date: saResult.from_date,
        to_date: saResult.to_date,
        language: saAiLang
      }, { timeout: 60000 });
      if (res.data.success && res.data.insights) {
        setSaAiInsights(res.data.insights);
        toast.success('AI insights generated');
      } else if (retryCount < 1) {
        toast.info('Retrying AI generation...');
        setSaAiLoading(false);
        return fetchAiInsights(retryCount + 1);
      } else {
        toast.error(res.data.error || 'AI analysis failed after retry');
      }
    } catch (err) {
      if (retryCount < 1) {
        toast.info('Connection issue, retrying...');
        setSaAiLoading(false);
        return fetchAiInsights(retryCount + 1);
      }
      toast.error('Failed to generate AI insights. Please try again.');
    }
    setSaAiLoading(false);
  };

  const createPOFromSupplierAnalysis = () => {
    const items = Object.values(saSelected).filter(s => s.selected && s.qty > 0);
    if (items.length === 0) { toast.error('Select at least one item with qty > 0'); return; }

    const supplier = supplierAccounts.find(s => s.id === saSupplier);
    const lines = items.map(item => ({
      ...emptyLine,
      inventory_item_id: item.item_id,
      item_name: item.item_name,
      item_name_ar: item.item_name_ar || '',
      barcode: item.barcode || '',
      unit: item.unit || 'piece',
      unit_price: item.unit_cost || 0,
      selling_price: item.selling_price || 0,
      currency: item.currency || 'EUR',
      discount_percent: item.discount_percent || 0,
      quantity: item.qty
    }));

    const newOrder = {
      ...emptyOrder,
      supplier_id: supplier?.id || '',
      supplier_name: supplier?.name || '',
      supplier_code: supplier?.code || '',
      lines,
      notes: `Generated from Supplier Analysis (${saFromDate} to ${saToDate})`
    };

    setOrder(recalcOrder(newOrder));
    setInventoryItems(prev => {
      const existingIds = new Set(prev.map(i => i.id));
      const newItems = items.filter(i => i.item_id && !existingIds.has(i.item_id)).map(i => ({
        id: i.item_id, name: i.item_name, name_ar: i.item_name_ar || '', barcode: i.barcode || '',
        cost: i.unit_cost || 0, price: i.selling_price || 0, unit: i.unit || 'piece'
      }));
      return newItems.length > 0 ? [...prev, ...newItems] : prev;
    });
    setActiveTab('list');
    setShowForm(true);
    toast.success(`Created PO draft with ${items.length} items for ${supplier?.name || 'supplier'}`);
  };

  // ==================== CRUD ====================
  const handleSave = async () => {
    if (!order.lines.some(l => l.item_name)) {
      toast.error('Add at least one line item');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...order, organization_id: currentOrg.id };
      if (editingOrder) {
        await axios.put(`${API}/purchase-orders/${editingOrder.id}`, payload);
        toast.success('Purchase order updated');
      } else {
        await axios.post(`${API}/purchase-orders`, payload);
        toast.success('Purchase order created');
      }
      resetForm();
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const handleStatusAction = async (orderId, action) => {
    try {
      await axios.put(`${API}/purchase-orders/${orderId}/status?action=${action}`);
      toast.success(`Order ${action}d`);
      fetchOrders();
      setViewOrder(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action}`);
    }
  };

  const handleRevertToDraft = async (orderId) => {
    if (!window.confirm('Are you sure you want to revert this order to Draft status? This will remove approval.')) {
      return;
    }
    try {
      await axios.put(`${API}/purchase-orders/${orderId}/status?action=revert_to_draft`);
      toast.success('Order reverted to Draft');
      fetchOrders();
      setViewOrder(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to revert');
    }
  };

  const handleUnpost = async (orderId) => {
    if (!window.confirm('Are you sure you want to unpost this order? This will revert it to "Delivered" status. Inventory updates and linked invoices will NOT be reversed.')) {
      return;
    }
    try {
      await axios.post(`${API}/purchase-orders/${orderId}/unpost`);
      toast.success('Order unposted - status reverted to Delivered');
      fetchOrders();
      setViewOrder(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unpost');
    }
  };

  const handleSubmitToSupplier = async (orderId) => {
    try {
      await axios.post(`${API}/purchase-orders/${orderId}/submit`);
      toast.success('Order submitted to supplier');
      fetchOrders();
      setViewOrder(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    }
  };

  const handlePost = async () => {
    if (!postDebitId || !postCreditId) {
      toast.error('Select both debit and credit accounts');
      return;
    }
    setPosting(true);
    try {
      await axios.post(
        `${API}/purchase-orders/${postingOrder.id}/post?debit_account_id=${postDebitId}&credit_account_id=${postCreditId}`
      );
      toast.success('Purchase order posted as invoice!');
      setShowPostDialog(false);
      setPostingOrder(null);
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to post');
    }
    setPosting(false);
  };

  const handleDelete = async (orderId, forceDelete = false) => {
    const message = forceDelete 
      ? 'This order is posted. Are you sure you want to FORCE DELETE it? This action cannot be undone.'
      : 'Delete this purchase order?';
    if (!window.confirm(message)) return;
    try {
      const url = forceDelete 
        ? `${API}/purchase-orders/${orderId}?force=true`
        : `${API}/purchase-orders/${orderId}`;
      await axios.delete(url);
      toast.success('Purchase order deleted');
      fetchOrders();
      setViewOrder(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const openEdit = (o) => {
    setEditingOrder(o);
    setOrder({ ...o });
    setShowForm(true);
    setActiveTab('list');
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingOrder(null);
    setOrder({ ...emptyOrder });
  };

  // ==================== GOODS RECEIPT ====================
  const openReceiveDialog = (o) => {
    setReceivingOrder(o);
    // Initialize received quantities from existing data or 0
    const qtyMap = {};
    const notesMap = {};
    o.lines.forEach(line => {
      const itemId = line.inventory_item_id || line.item_id;
      qtyMap[itemId] = line.received_qty || 0;
      notesMap[itemId] = line.receive_notes || '';
    });
    setReceivedQtys(qtyMap);
    setReceiveNotes(notesMap);
    setActiveTab('receive');
  };

  const submitGoodsReceipt = async () => {
    if (!receivingOrder) return;
    setSubmittingReceipt(true);
    try {
      const received_lines = receivingOrder.lines.map((line, idx) => {
        const itemId = line.inventory_item_id || line.item_id;
        return {
          item_id: itemId,
          line_index: idx, // Fallback for items without item_id
          received_qty: receivedQtys[itemId] || 0,
          notes: receiveNotes[itemId] || ''
        };
      });

      await axios.post(`${API}/purchase-orders/${receivingOrder.id}/receive`, {
        received_lines
      });

      toast.success('Goods receipt recorded');
      setReceivingOrder(null);
      setActiveTab('list');
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record receipt');
    }
    setSubmittingReceipt(false);
  };

  const markAllReceived = () => {
    if (!receivingOrder) return;
    const qtyMap = {};
    receivingOrder.lines.forEach(line => {
      const itemId = line.inventory_item_id || line.item_id;
      qtyMap[itemId] = line.quantity;
    });
    setReceivedQtys(qtyMap);
  };

  const [creatingInvoice, setCreatingInvoice] = useState(false);
  
  const createPurchaseInvoice = async (orderId) => {
    setCreatingInvoice(true);
    try {
      const res = await axios.post(`${API}/purchase-orders/${orderId}/to-purchase-invoice`);
      toast.success(res.data.message);
      setReceivingOrder(null);
      setActiveTab('list');
      fetchOrders();
      // Optionally navigate to the new invoice
      // window.location.href = `/purchase-invoices?edit=${res.data.invoice.id}`;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create purchase invoice');
    }
    setCreatingInvoice(false);
  };

  // ==================== DISCREPANCY REPORT ====================
  const downloadDiscrepancyPDF = async (orderId) => {
    try {
      const res = await axios.get(`${API}/purchase-orders/${orderId}/discrepancy-report`);
      const report = res.data;

      // Generate PDF using browser print
      const html = generateDiscrepancyHTML(report);
      const w = window.open('', '_blank', 'width=900,height=700');
      w.document.write(html);
      w.document.close();
      setTimeout(() => w.print(), 300);
    } catch (err) {
      toast.error('Failed to generate report');
    }
  };

  const generateDiscrepancyHTML = (report) => {
    const hasDiscrepancies = report.discrepancies_only.length > 0;
    
    return `<!DOCTYPE html>
<html><head><title>Discrepancy Report - ${report.order_number}</title>
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  body{font-family:Arial,sans-serif;font-size:11px;padding:15mm;color:#000}
  .header{text-align:center;margin-bottom:8mm;border-bottom:2px solid #000;padding-bottom:4mm}
  .header h1{font-size:18px;margin-bottom:2mm;color:#c00}
  .header h2{font-size:14px;color:#333}
  .meta{display:flex;justify-content:space-between;margin:5mm 0;font-size:10px}
  .meta div{flex:1}
  .summary{background:#f5f5f5;padding:4mm;margin:5mm 0;border-radius:3px}
  .summary-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:3mm}
  .summary-item{text-align:center}
  .summary-item .label{font-size:9px;color:#666}
  .summary-item .value{font-size:14px;font-weight:bold}
  .summary-item .value.negative{color:#c00}
  .summary-item .value.positive{color:#090}
  table{width:100%;border-collapse:collapse;margin:5mm 0}
  th,td{border:1px solid #999;padding:2mm;text-align:left;font-size:10px}
  th{background:#e0e0e0;font-weight:bold}
  .text-right{text-align:right}
  .text-center{text-align:center}
  .full{background:#d4edda;color:#155724}
  .partial{background:#fff3cd;color:#856404}
  .not-received{background:#f8d7da;color:#721c24}
  .variance-neg{color:#c00;font-weight:bold}
  .variance-pos{color:#090;font-weight:bold}
  .footer{margin-top:10mm;font-size:9px;color:#666;text-align:center;border-top:1px solid #ccc;padding-top:3mm}
  .no-discrepancy{text-align:center;padding:10mm;background:#d4edda;color:#155724;border-radius:3px;margin:5mm 0}
  @media print{body{padding:10mm}@page{size:A4;margin:10mm}}
</style></head><body>
<div class="header">
  <h1>DISCREPANCY REPORT</h1>
  <h2>${report.order_number}</h2>
  <p style="margin-top:2mm;color:#666">تقرير الفروقات</p>
</div>

<div class="meta">
  <div>
    <strong>Supplier:</strong> ${report.supplier_name || 'N/A'}<br/>
    <strong>Order Date:</strong> ${formatDate(report.date)}<br/>
    <strong>Status:</strong> ${report.status?.toUpperCase()}
  </div>
  <div style="text-align:right">
    <strong>Report Generated:</strong> ${new Date().toLocaleString()}<br/>
    <strong>Total Lines:</strong> ${report.summary.total_lines}<br/>
    <strong>Lines with Issues:</strong> ${report.summary.lines_with_discrepancy}
  </div>
</div>

<div class="summary">
  <h3 style="margin-bottom:3mm;font-size:12px">Summary</h3>
  <div class="summary-grid">
    <div class="summary-item">
      <div class="label">Total Ordered Qty</div>
      <div class="value">${report.summary.total_ordered_qty}</div>
    </div>
    <div class="summary-item">
      <div class="label">Total Received Qty</div>
      <div class="value">${report.summary.total_received_qty}</div>
    </div>
    <div class="summary-item">
      <div class="label">Variance Qty</div>
      <div class="value ${report.summary.total_variance_qty < 0 ? 'negative' : report.summary.total_variance_qty > 0 ? 'positive' : ''}">${report.summary.total_variance_qty}</div>
    </div>
    <div class="summary-item">
      <div class="label">Total Ordered Value</div>
      <div class="value">$${report.summary.total_ordered_value.toFixed(2)}</div>
    </div>
    <div class="summary-item">
      <div class="label">Total Received Value</div>
      <div class="value">$${report.summary.total_received_value.toFixed(2)}</div>
    </div>
    <div class="summary-item">
      <div class="label">Variance Value</div>
      <div class="value ${report.summary.total_variance_value < 0 ? 'negative' : report.summary.total_variance_value > 0 ? 'positive' : ''}">$${report.summary.total_variance_value.toFixed(2)}</div>
    </div>
  </div>
</div>

${hasDiscrepancies ? `
<h3 style="margin:5mm 0 2mm;font-size:12px;color:#c00">Items with Discrepancies (${report.discrepancies_only.length})</h3>
<table>
  <thead>
    <tr>
      <th>#</th>
      <th>Item Code</th>
      <th>Item Name</th>
      <th>Unit</th>
      <th class="text-right">Ordered</th>
      <th class="text-right">Received</th>
      <th class="text-right">Variance</th>
      <th class="text-right">Unit Cost</th>
      <th class="text-right">Value Diff</th>
      <th class="text-center">Status</th>
    </tr>
  </thead>
  <tbody>
    ${report.discrepancies_only.map((item, i) => `
    <tr>
      <td>${i + 1}</td>
      <td>${item.item_code || '-'}</td>
      <td>${item.item_name}</td>
      <td>${item.unit}</td>
      <td class="text-right">${item.ordered_qty}</td>
      <td class="text-right">${item.received_qty}</td>
      <td class="text-right ${item.variance_qty < 0 ? 'variance-neg' : 'variance-pos'}">${item.variance_qty > 0 ? '+' : ''}${item.variance_qty}</td>
      <td class="text-right">$${item.unit_cost.toFixed(3)}</td>
      <td class="text-right ${item.variance_value < 0 ? 'variance-neg' : 'variance-pos'}">$${item.variance_value.toFixed(2)}</td>
      <td class="text-center"><span class="${item.status === 'FULL' ? 'full' : item.status === 'PARTIAL' ? 'partial' : 'not-received'}">${item.status}</span></td>
    </tr>
    `).join('')}
  </tbody>
</table>
` : `
<div class="no-discrepancy">
  <strong>No Discrepancies Found</strong><br/>
  All items received as ordered.
</div>
`}

<h3 style="margin:8mm 0 2mm;font-size:12px">All Items</h3>
<table>
  <thead>
    <tr>
      <th>#</th>
      <th>Item Name</th>
      <th class="text-right">Ordered</th>
      <th class="text-right">Received</th>
      <th class="text-center">Status</th>
      <th>Notes</th>
    </tr>
  </thead>
  <tbody>
    ${report.all_items.map((item, i) => `
    <tr>
      <td>${i + 1}</td>
      <td>${item.item_name}</td>
      <td class="text-right">${item.ordered_qty}</td>
      <td class="text-right">${item.received_qty}</td>
      <td class="text-center"><span class="${item.status === 'FULL' ? 'full' : item.status === 'PARTIAL' ? 'partial' : 'not-received'}">${item.status}</span></td>
      <td>${item.notes || '-'}</td>
    </tr>
    `).join('')}
  </tbody>
</table>

<div class="footer">
  <p>Generated by Pharmacy System | ${new Date().toLocaleDateString()}</p>
</div>
</body></html>`;
  };

  // Print
  const printOrder = (o) => {
    const html = `<!DOCTYPE html><html><head><title>PO ${o.order_number}</title>
    <style>
      *{margin:0;padding:0;box-sizing:border-box}
      body{font-family:Arial,sans-serif;font-size:12px;padding:15mm;color:#000}
      .header{text-align:center;margin-bottom:8mm;border-bottom:2px solid #000;padding-bottom:4mm}
      .header h1{font-size:20px;margin-bottom:2mm}
      .header h2{font-size:16px;color:#333}
      .meta{display:flex;justify-content:space-between;margin:5mm 0;font-size:11px}
      .meta div{flex:1}
      table{width:100%;border-collapse:collapse;margin:5mm 0}
      th,td{border:1px solid #999;padding:3mm 2mm;text-align:left;font-size:11px}
      th{background:#f0f0f0;font-weight:bold}
      .text-right{text-align:right}
      .totals{width:50%;margin-left:auto;margin-top:5mm}
      .totals td{border:none;padding:1.5mm 2mm}
      .totals .grand{font-size:14px;font-weight:bold;border-top:2px solid #000}
      .footer{margin-top:10mm;font-size:10px;color:#666;text-align:center}
      .badge{display:inline-block;padding:1mm 3mm;border-radius:3px;font-size:10px;font-weight:bold;background:#e0e0e0}
      .badge-draft{background:#ccc;color:#333}
      .badge-approved{background:#c8e6ff;color:#0066cc}
      .badge-sent{background:#e8d5f5;color:#7700cc}
      .badge-received{background:#ffeacc;color:#cc6600}
      .badge-posted{background:#d4edda;color:#006600}
      @media print{body{padding:10mm}@page{size:A4;margin:10mm}}
    </style></head><body>
    <div class="header">
      <h1>PURCHASE ORDER</h1>
      <h2>${o.order_number}</h2>
      <span class="badge badge-${o.status}">${(o.status || '').toUpperCase()}</span>
    </div>
    <div class="meta">
      <div><strong>Date:</strong> ${formatDate(o.date)}<br/>
      ${o.expected_delivery_date ? `<strong>Expected Delivery:</strong> ${formatDate(o.expected_delivery_date)}<br/>` : ''}
      <strong>Type:</strong> ${o.order_type === 'daily_sales' ? 'Daily Sales Order' : 'Supplier Order'}</div>
      <div style="text-align:right">
      ${o.supplier_name ? `<strong>Supplier:</strong> ${o.supplier_code ? `[${o.supplier_code}] ` : ''}${o.supplier_name}<br/>` : ''}
      ${o.purchase_invoice_number ? `<strong>Invoice:</strong> ${o.purchase_invoice_number}<br/>` : ''}
      </div>
    </div>
    <table>
      <thead><tr><th>#</th><th>Item</th><th>Barcode</th><th>Qty</th><th>Unit</th><th>Price</th><th>Sell Price</th><th>Disc%</th><th class="text-right">Total</th></tr></thead>
      <tbody>
      ${o.lines.map((l, i) => {
        const curr = l.currency || o.currency || 'EUR';
        const prefix = curr === 'EUR' ? 'EUR ' : 'FCFA ';
        return `<tr>
        <td>${i + 1}</td><td>${l.item_name}${l.item_name_ar ? `<br/><small>${l.item_name_ar}</small>` : ''}</td>
        <td>${l.barcode || '-'}</td><td>${l.quantity}</td><td>${l.unit}</td>
        <td>${prefix}${parseFloat(l.unit_price).toLocaleString()}</td>
        <td>${l.selling_price ? `${prefix}${parseFloat(l.selling_price).toLocaleString()}` : '-'}</td>
        <td>${l.discount_percent || 0}%</td>
        <td class="text-right">${prefix}${(l.line_total || 0).toLocaleString()}</td>
      </tr>`;
      }).join('')}
      </tbody>
    </table>
    <table class="totals">
      ${(() => {
        const hasMixed = o.lines?.some(l => (l.currency || o.currency) === 'XOF') && o.lines?.some(l => (l.currency || o.currency) === 'EUR');
        if (hasMixed) {
          return `
          <tr><td>Subtotal XOF:</td><td class="text-right">$${(o.subtotal_usd || o.total_usd || 0).toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})}</td></tr>
          <tr><td>Subtotal EUR:</td><td class="text-right">EUR ${(o.subtotal_lbp || o.total_lbp || 0).toLocaleString()}</td></tr>
          <tr class="grand"><td>Total XOF:</td><td class="text-right">$${(o.total_usd || 0).toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})}</td></tr>
          <tr class="grand"><td>Total EUR:</td><td class="text-right">EUR ${(o.total_lbp || 0).toLocaleString()}</td></tr>
          `;
        }
        const curr = o.currency || 'EUR';
        const prefix = curr === 'EUR' ? 'EUR ' : 'FCFA ';
        return `
        <tr><td>Subtotal:</td><td class="text-right">${prefix}${(o.subtotal || 0).toLocaleString()}</td></tr>
        ${o.discount_amount > 0 ? `<tr><td>Discount (${o.discount_percent}%):</td><td class="text-right">-${prefix}${o.discount_amount.toLocaleString()}</td></tr>` : ''}
        ${o.tax_amount > 0 ? `<tr><td>VAT (${o.tax_percent}%):</td><td class="text-right">+${prefix}${o.tax_amount.toLocaleString()}</td></tr>` : ''}
        <tr class="grand"><td>TOTAL:</td><td class="text-right">${prefix}${(o.total || 0).toLocaleString()}</td></tr>
        `;
      })()}
    </table>
    ${o.notes ? `<div style="margin-top:5mm;padding:3mm;background:#f9f9f9;border:1px solid #ddd"><strong>Notes:</strong> ${o.notes}</div>` : ''}
    <div class="footer">
      <p>Printed on ${new Date().toLocaleString()}</p>
    </div>
    </body></html>`;
    const w = window.open('', '_blank', 'width=800,height=900');
    w.document.write(html);
    w.document.close();
    setTimeout(() => w.print(), 300);
  };

  // Filtered list
  const filteredOrders = useMemo(() => {
    if (!searchTerm) return orders;
    const s = searchTerm.toLowerCase();
    return orders.filter(o =>
      o.order_number?.toLowerCase().includes(s) ||
      o.supplier_name?.toLowerCase().includes(s) ||
      o.notes?.toLowerCase().includes(s)
    );
  }, [orders, searchTerm]);

  if (!currentOrg) return <div className="p-8 text-muted-foreground text-center">Select an organization</div>;

  // ==================== FORM VIEW ====================
  if (showForm) {
    return (
      <div className="space-y-4 pb-24" data-testid="po-form">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h1 className="text-xl font-bold flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-primary" />
            {editingOrder ? `Edit ${editingOrder.order_number}` : 'New Purchase Order'}
          </h1>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={resetForm}><X className="w-4 h-4 mr-1" />Cancel</Button>
            <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-po-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save Draft
            </Button>
          </div>
        </div>

        {/* Header Fields */}
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Date</Label>
                <DateInput value={order.date} onChange={v => setOrder(prev => ({ ...prev, date: v }))} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Expected Delivery</Label>
                <DateInput value={order.expected_delivery_date} onChange={v => setOrder(prev => ({ ...prev, expected_delivery_date: v }))} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Order Type</Label>
                <Select value={order.order_type} onValueChange={v => setOrder(prev => ({ ...prev, order_type: v }))}>
                  <SelectTrigger className="h-9" data-testid="order-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="supplier">Supplier Order</SelectItem>
                    <SelectItem value="daily_sales">Daily Sales Order</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1 col-span-2">
                <Label className="text-xs">Supplier</Label>
                <SearchableSupplierSelect
                  suppliers={supplierAccounts}
                  value={order.supplier_id}
                  onValueChange={selectSupplier}
                  placeholder="Search supplier..."
                  testId="supplier-select"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Low Stock Suggestions */}
        {lowStockItems.length > 0 && (
          <Card className="border-amber-500/30">
            <CardContent className="p-3">
              <button
                onClick={() => setShowLowStock(!showLowStock)}
                className="flex items-center gap-2 w-full text-left"
              >
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                <span className="text-sm font-medium text-amber-400">Low Stock Items ({lowStockItems.length})</span>
                {showLowStock ? <ChevronUp className="w-4 h-4 ml-auto" /> : <ChevronDown className="w-4 h-4 ml-auto" />}
              </button>
              {showLowStock && (
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {lowStockItems.map(item => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between p-2 rounded bg-muted/30 border border-border cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => addLowStockItem(item)}
                    >
                      <div className="min-w-0">
                        <p className="text-xs font-medium truncate">{item.name}</p>
                        <p className="text-[10px] text-muted-foreground">
                          Stock: {item.on_hand_qty || 0} / Min: {item.reorder_level || 5}
                        </p>
                      </div>
                      <Plus className="w-4 h-4 flex-shrink-0 text-primary" />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Line Items */}
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-medium">Line Items</Label>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={fetchSupplierItems} disabled={!order.supplier_id || fetchingItems} data-testid="fetch-supplier-items-btn">
                  <FileDown className="w-3 h-3 mr-1" />{fetchingItems ? 'Loading...' : 'Fetch Supplier Items'}
                </Button>
                <Button variant="outline" size="sm" onClick={addLine}><Plus className="w-3 h-3 mr-1" />Add</Button>
              </div>
            </div>

            {/* Desktop table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/20">
                    <th className="p-2 text-left w-6">#</th>
                    <th className="p-2 text-left min-w-[160px]">Item</th>
                    <th className="p-2 text-right w-32">Sell Price</th>
                    <th className="p-2 text-right w-24">Margin%</th>
                    <th className="p-2 text-right w-32">Cost</th>
                    <th className="p-2 text-right w-16">Qty</th>
                    <th className="p-2 text-right w-14">Free</th>
                    <th className="p-2 text-center w-16">Curr</th>
                    <th className="p-2 w-24">Batch</th>
                    <th className="p-2 w-32">Expiry</th>
                    <th className="p-2 text-right w-28">Total EUR</th>
                    <th className="p-2 text-right w-24">Total XOF</th>
                    <th className="p-2 w-6"></th>
                  </tr>
                </thead>
                <tbody>
                  {order.lines.map((line, idx) => (
                    <tr key={idx} className="border-b hover:bg-muted/10">
                      <td className="p-1 text-muted-foreground">{idx + 1}</td>
                      <td className="p-1">
                        <InventorySelector
                          items={inventoryItems}
                          value={line.inventory_item_id}
                          onChange={(val) => {
                            const item = inventoryItems.find(i => i.id === val);
                            if (item) selectInventoryItem(idx, item);
                          }}
                          onItemSelect={(item) => selectInventoryItem(idx, item)}
                          placeholder="Search item..."
                          displayValue={line.item_name}
                          apiUrl={API}
                          organizationId={currentOrg?.id}
                        />
                      </td>
                      <td className="p-1"><Input type="number" className="h-7 text-xs text-right font-mono w-full min-w-[100px]" value={line.selling_price || ''} onChange={e => updateLine(idx, 'selling_price', e.target.value)} min="0" step="0.01" /></td>
                      <td className="p-1"><Input type="number" className="h-7 text-xs text-right w-full" value={line.margin_percent || ''} onChange={e => updateLine(idx, 'margin_percent', e.target.value)} min="0" max="100" step="0.1" /></td>
                      <td className="p-1"><Input type="number" className="h-7 text-xs text-right font-mono w-full min-w-[100px]" value={line.unit_price} onChange={e => updateLine(idx, 'unit_price', e.target.value)} min="0" step="0.001" /></td>
                      <td className="p-1"><Input type="number" className="h-7 text-xs text-right w-full" value={line.quantity} onChange={e => updateLine(idx, 'quantity', e.target.value)} min="0" step="1" /></td>
                      <td className="p-1"><Input type="number" className="h-7 text-xs text-right w-full" value={line.free_qty || ''} onChange={e => updateLine(idx, 'free_qty', e.target.value)} min="0" step="1" /></td>
                      <td className="p-1 text-center">
                        <Select value={line.currency || 'EUR'} onValueChange={v => updateLine(idx, 'currency', v)}>
                          <SelectTrigger className="h-7 text-xs w-16"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="EUR">EUR</SelectItem>
                            <SelectItem value="XOF">XOF</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-1"><Input className="h-7 text-xs w-full" value={line.batch_number || ''} onChange={e => updateLine(idx, 'batch_number', e.target.value)} placeholder="Batch" /></td>
                      <td className="p-1"><DateInput value={line.expiry_date || ''} onChange={(val) => updateLine(idx, 'expiry_date', val)} className="h-7 text-xs w-full min-w-[110px]" /></td>
                      <td className="p-1 text-right font-mono text-[10px] whitespace-nowrap">{(line.line_total_lbp || 0).toLocaleString()}</td>
                      <td className="p-1 text-right font-mono text-[10px] whitespace-nowrap">${(line.line_total_usd || 0).toFixed(2)}</td>
                      <td className="p-1">
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeLine(idx)} disabled={order.lines.length <= 1}>
                          <Trash2 className="w-3 h-3 text-destructive" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="lg:hidden space-y-3">
              {order.lines.map((line, idx) => (
                <Card key={idx} className="bg-muted/10">
                  <CardContent className="p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground font-medium">Item #{idx + 1}</span>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeLine(idx)} disabled={order.lines.length <= 1}>
                        <Trash2 className="w-3 h-3 text-destructive" />
                      </Button>
                    </div>
                    <InventorySelector
                      items={inventoryItems}
                      value={line.inventory_item_id}
                      onChange={(val) => {
                        const item = inventoryItems.find(i => i.id === val);
                        if (item) selectInventoryItem(idx, item);
                      }}
                      onItemSelect={(item) => selectInventoryItem(idx, item)}
                      placeholder="Search item..."
                      apiUrl={API}
                      organizationId={currentOrg?.id}
                    />
                    <div className="grid grid-cols-3 gap-2">
                      <div><Label className="text-[10px]">Sell Price</Label><Input type="number" className="h-8 text-sm" value={line.selling_price || ''} onChange={e => updateLine(idx, 'selling_price', e.target.value)} /></div>
                      <div><Label className="text-[10px]">Margin%</Label><Input type="number" className="h-8 text-sm" value={line.margin_percent || ''} onChange={e => updateLine(idx, 'margin_percent', e.target.value)} /></div>
                      <div><Label className="text-[10px]">Cost</Label><Input type="number" className="h-8 text-sm" value={line.unit_price} onChange={e => updateLine(idx, 'unit_price', e.target.value)} /></div>
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      <div><Label className="text-[10px]">Qty</Label><Input type="number" className="h-8 text-sm" value={line.quantity} onChange={e => updateLine(idx, 'quantity', e.target.value)} /></div>
                      <div><Label className="text-[10px]">Free</Label><Input type="number" className="h-8 text-sm" value={line.free_qty || ''} onChange={e => updateLine(idx, 'free_qty', e.target.value)} /></div>
                      <div><Label className="text-[10px]">Batch</Label><Input className="h-8 text-sm" value={line.batch_number || ''} onChange={e => updateLine(idx, 'batch_number', e.target.value)} /></div>
                      <div><Label className="text-[10px]">Expiry</Label><DateInput value={line.expiry_date || ''} onChange={(val) => updateLine(idx, 'expiry_date', val)} className="h-8 text-sm" /></div>
                    </div>
                    <div className="flex justify-between text-right font-mono text-sm font-medium">
                      <span className="text-muted-foreground">{(line.line_total_lbp || 0).toLocaleString()} EUR</span>
                      <span className="text-primary">${(line.line_total_usd || 0).toFixed(2)}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
              <Button variant="outline" className="w-full" onClick={addLine}><Plus className="w-4 h-4 mr-1" />Add Item</Button>
            </div>
          </CardContent>
        </Card>

        {/* Totals & Notes */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <CardContent className="p-4 space-y-2">
              <Label className="text-xs">Notes</Label>
              <Textarea
                value={order.notes || ''} rows={3}
                onChange={e => setOrder(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Order notes, supplier ref, delivery instructions..."
              />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 space-y-2">
              {order.has_mixed_currency ? (
                <>
                  <div className="flex justify-between text-sm"><span>Subtotal XOF:</span><span className="font-mono">${(order.subtotal_usd || 0).toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})}</span></div>
                  <div className="flex justify-between text-sm"><span>Subtotal EUR:</span><span className="font-mono">EUR {(order.subtotal_lbp || 0).toLocaleString()}</span></div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2"><span>Total XOF:</span><span className="font-mono text-primary">${(order.total_usd || 0).toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})}</span></div>
                  <div className="flex justify-between text-lg font-bold"><span>Total EUR:</span><span className="font-mono text-primary">EUR {(order.total_lbp || 0).toLocaleString()}</span></div>
                </>
              ) : (
                <>
                  <div className="flex justify-between text-sm"><span>Subtotal:</span><span className="font-mono">{order.subtotal?.toLocaleString()} {order.currency === 'XOF' ? 'XOF' : 'EUR'}</span></div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm flex-1">Discount:</span>
                    <Input type="number" className="h-7 w-16 text-xs text-right" value={order.discount_percent} onChange={e => setOrder(recalcOrder({ ...order, discount_percent: e.target.value }))} min="0" max="100" />
                    <span className="text-xs">%</span>
                    <span className="font-mono text-sm">-{order.discount_amount?.toLocaleString()}</span>
                  </div>
                  {isVatRegistered() && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm flex-1">VAT:</span>
                    <Input type="number" className="h-7 w-16 text-xs text-right" value={order.tax_percent} onChange={e => setOrder(recalcOrder({ ...order, tax_percent: e.target.value }))} min="0" max="100" />
                    <span className="text-xs">%</span>
                    <span className="font-mono text-sm">+{order.tax_amount?.toLocaleString()}</span>
                  </div>
                  )}
                  <div className="flex justify-between text-lg font-bold border-t pt-2"><span>Total:</span><span className="font-mono text-primary">{order.total?.toLocaleString()} {order.currency === 'XOF' ? 'XOF' : 'EUR'}</span></div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Mobile sticky save */}
        <div className="fixed bottom-0 left-0 right-0 lg:hidden p-3 bg-background/95 backdrop-blur border-t z-40 flex gap-2">
          <Button variant="outline" className="flex-1" onClick={resetForm}>Cancel</Button>
          <Button className="flex-1" onClick={handleSave} disabled={saving} data-testid="save-po-btn-mobile">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Save Draft
          </Button>
        </div>
      </div>
    );
  }

  // ==================== GOODS RECEIPT VIEW ====================
  if (activeTab === 'receive' && receivingOrder) {
    const totalOrdered = receivingOrder.lines.reduce((s, l) => s + (l.quantity || 0), 0);
    const totalReceived = Object.values(receivedQtys).reduce((s, q) => s + (q || 0), 0);

    return (
      <div className="space-y-4 pb-24" data-testid="goods-receipt-form">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Package className="w-5 h-5 text-primary" />
              Goods Receipt - {receivingOrder.order_number}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Supplier: {receivingOrder.supplier_name || 'N/A'} | Date: {formatDate(receivingOrder.date)}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="ghost" size="sm" onClick={() => { setReceivingOrder(null); setActiveTab('list'); }}>
              <X className="w-4 h-4 mr-1" />Cancel
            </Button>
            <Button variant="outline" size="sm" onClick={markAllReceived}>
              <CheckCircle2 className="w-4 h-4 mr-1" />Mark All Received
            </Button>
            <Button size="sm" onClick={submitGoodsReceipt} disabled={submittingReceipt} data-testid="submit-receipt-btn">
              {submittingReceipt ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save Receipt
            </Button>
            {['partial_received', 'delivered', 'received'].includes(receivingOrder.status) && !receivingOrder.purchase_invoice_id && (
              <Button 
                size="sm" 
                variant="default"
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => createPurchaseInvoice(receivingOrder.id)} 
                disabled={creatingInvoice}
                data-testid="create-invoice-btn"
              >
                {creatingInvoice ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
                Create Purchase Invoice
              </Button>
            )}
            {receivingOrder.purchase_invoice_number && (
              <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 self-center">
                Invoice: {receivingOrder.purchase_invoice_number}
              </Badge>
            )}
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card>
            <CardContent className="p-3 text-center">
              <p className="text-xs text-muted-foreground">Total Lines</p>
              <p className="text-2xl font-bold">{receivingOrder.lines.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <p className="text-xs text-muted-foreground">Total Ordered</p>
              <p className="text-2xl font-bold">{totalOrdered}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <p className="text-xs text-muted-foreground">Total Received</p>
              <p className="text-2xl font-bold text-primary">{totalReceived}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <p className="text-xs text-muted-foreground">Variance</p>
              <p className={`text-2xl font-bold ${totalReceived - totalOrdered < 0 ? 'text-red-500' : totalReceived - totalOrdered > 0 ? 'text-emerald-500' : ''}`}>
                {totalReceived - totalOrdered}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Receipt Lines */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto -mx-2 px-2">
              <table className="w-full text-sm min-w-[700px]" data-testid="receipt-lines-table">
                <thead>
                  <tr className="border-b bg-muted/20">
                    <th className="p-3 text-left">#</th>
                    <th className="p-3 text-left">Item</th>
                    <th className="p-3 text-left">Barcode</th>
                    <th className="p-3 text-center">Ordered</th>
                    <th className="p-3 text-center">Received</th>
                    <th className="p-3 text-center">Status</th>
                    <th className="p-3 text-left">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {receivingOrder.lines.map((line, idx) => {
                    const itemId = line.inventory_item_id || line.item_id;
                    const orderedQty = line.quantity || 0;
                    const receivedQty = receivedQtys[itemId] || 0;
                    
                    let status = 'NOT_RECEIVED';
                    if (receivedQty >= orderedQty) status = 'FULL';
                    else if (receivedQty > 0) status = 'PARTIAL';

                    return (
                      <tr key={idx} className="border-b hover:bg-muted/10">
                        <td className="p-3 text-muted-foreground">{idx + 1}</td>
                        <td className="p-3">
                          <div>
                            <p className="font-medium">{line.item_name}</p>
                            {line.item_name_ar && <p className="text-xs text-muted-foreground">{line.item_name_ar}</p>}
                          </div>
                        </td>
                        <td className="p-3 font-mono text-xs">{line.barcode || '-'}</td>
                        <td className="p-3 text-center font-bold">{orderedQty}</td>
                        <td className="p-3">
                          <Input
                            type="number"
                            className="h-9 w-24 mx-auto text-center"
                            value={receivedQty}
                            onChange={e => setReceivedQtys(prev => ({ ...prev, [itemId]: parseInt(e.target.value) || 0 }))}
                            min="0"
                            data-testid={`received-qty-${idx}`}
                          />
                        </td>
                        <td className="p-3 text-center">
                          <Badge className={lineStatusColors[status]}>
                            {status === 'FULL' && <CheckCircle2 className="w-3 h-3 mr-1" />}
                            {status === 'PARTIAL' && <MinusCircle className="w-3 h-3 mr-1" />}
                            {status === 'NOT_RECEIVED' && <XCircle className="w-3 h-3 mr-1" />}
                            {status}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <Input
                            className="h-9"
                            placeholder="Notes..."
                            value={receiveNotes[itemId] || ''}
                            onChange={e => setReceiveNotes(prev => ({ ...prev, [itemId]: e.target.value }))}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Mobile sticky save */}
        <div className="fixed bottom-0 left-0 right-0 lg:hidden p-3 bg-background/95 backdrop-blur border-t z-40 flex gap-2">
          <Button variant="outline" className="flex-1" onClick={() => { setReceivingOrder(null); setActiveTab('list'); }}>Cancel</Button>
          <Button className="flex-1" onClick={submitGoodsReceipt} disabled={submittingReceipt}>
            {submittingReceipt ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Save Receipt
          </Button>
        </div>
      </div>
    );
  }

  // ==================== VIEW DIALOG ====================
  const renderViewDialog = () => viewOrder && (
    <Dialog open={!!viewOrder} onOpenChange={() => setViewOrder(null)}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="po-view-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5" />
            {viewOrder.order_number}
            <Badge className={statusColors[viewOrder.status]}>{statusLabels[viewOrder.status]}</Badge>
          </DialogTitle>
          <DialogDescription>
            {viewOrder.order_type === 'daily_sales' ? 'Daily Sales Order' : 'Supplier Order'}
            {viewOrder.supplier_name && ` - ${viewOrder.supplier_name}`}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <div><span className="text-muted-foreground text-xs">Date</span><p>{formatDate(viewOrder.date)}</p></div>
          {viewOrder.expected_delivery_date && <div><span className="text-muted-foreground text-xs">Delivery</span><p>{formatDate(viewOrder.expected_delivery_date)}</p></div>}
          <div><span className="text-muted-foreground text-xs">Total</span>
            {(viewOrder.has_mixed_currency || (viewOrder.total_usd > 0 && viewOrder.total_lbp > 0)) ? (
              <div>
                <p className="font-mono text-primary font-bold">${(viewOrder.total_usd || 0).toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})}</p>
                <p className="font-mono text-xs text-muted-foreground">EUR {(viewOrder.total_lbp || 0).toLocaleString()}</p>
              </div>
            ) : (
              <p className="font-mono text-primary font-bold">{formatCurrency(viewOrder.total, viewOrder.currency)}</p>
            )}
          </div>
          {viewOrder.purchase_invoice_number && <div><span className="text-muted-foreground text-xs">Invoice</span><p className="text-emerald-400">{viewOrder.purchase_invoice_number}</p></div>}
        </div>

        <div className="overflow-x-auto mt-2 -mx-2 px-2">
          <table className="w-full text-xs min-w-[800px]">
            <thead><tr className="border-b bg-muted/20">
              <th className="p-2 text-left">#</th><th className="p-2 text-left">Item</th>
              <th className="p-2 text-right">Qty</th><th className="p-2">Unit</th>
              <th className="p-2 text-right">Cost</th><th className="p-2 text-right">Sell</th>
              <th className="p-2 text-right">Disc%</th><th className="p-2 text-right">Total</th>
              {['submitted', 'partial_received', 'delivered', 'received'].includes(viewOrder.status) && (
                <>
                  <th className="p-2 text-right">Received</th>
                  <th className="p-2 text-center">Status</th>
                </>
              )}
            </tr></thead>
            <tbody>
              {viewOrder.lines.map((l, i) => (
                <tr key={i} className="border-b">
                  <td className="p-2">{i + 1}</td>
                  <td className="p-2">{l.item_name}{l.batch_number ? <span className="text-muted-foreground ml-1">[{l.batch_number}]</span> : ''}</td>
                  <td className="p-2 text-right">{l.quantity}</td><td className="p-2">{l.unit}</td>
                  <td className="p-2 text-right font-mono">{formatCurrency(l.unit_price, l.currency || viewOrder.currency)}</td>
                  <td className="p-2 text-right font-mono">{l.selling_price ? formatCurrency(l.selling_price, l.currency || viewOrder.currency) : '-'}</td>
                  <td className="p-2 text-right">{l.discount_percent || 0}%</td>
                  <td className="p-2 text-right font-mono">{formatCurrency(l.line_total, l.currency || viewOrder.currency)}</td>
                  {['submitted', 'partial_received', 'delivered', 'received'].includes(viewOrder.status) && (
                    <>
                      <td className="p-2 text-right font-mono">{l.received_qty ?? '-'}</td>
                      <td className="p-2 text-center">
                        {l.line_status && (
                          <Badge className={lineStatusColors[l.line_status] + ' text-[9px]'}>
                            {l.line_status}
                          </Badge>
                        )}
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col items-end text-sm mt-2 space-y-1">
          {(viewOrder.has_mixed_currency || (viewOrder.subtotal_usd > 0 && viewOrder.subtotal_lbp > 0 && viewOrder.lines?.some(l => l.currency === 'XOF') && viewOrder.lines?.some(l => l.currency === 'EUR'))) ? (
            <>
              <div className="flex gap-8"><span className="text-muted-foreground">Subtotal XOF:</span><span className="font-mono">${(viewOrder.subtotal_usd || 0).toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})}</span></div>
              <div className="flex gap-8"><span className="text-muted-foreground">Subtotal EUR:</span><span className="font-mono">EUR {(viewOrder.subtotal_lbp || 0).toLocaleString()}</span></div>
              <div className="flex gap-8 font-bold text-base border-t pt-1"><span>Total XOF:</span><span className="font-mono text-primary">${(viewOrder.total_usd || 0).toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})}</span></div>
              <div className="flex gap-8 font-bold text-base"><span>Total EUR:</span><span className="font-mono text-primary">EUR {(viewOrder.total_lbp || 0).toLocaleString()}</span></div>
            </>
          ) : (
            <>
              <div className="flex gap-8"><span className="text-muted-foreground">Subtotal:</span><span className="font-mono">{formatCurrency(viewOrder.subtotal, viewOrder.currency)}</span></div>
              {viewOrder.discount_amount > 0 && <div className="flex gap-8"><span className="text-muted-foreground">Discount ({viewOrder.discount_percent}%):</span><span className="font-mono">-{formatCurrency(viewOrder.discount_amount, viewOrder.currency)}</span></div>}
              {viewOrder.tax_amount > 0 && <div className="flex gap-8"><span className="text-muted-foreground">VAT ({viewOrder.tax_percent}%):</span><span className="font-mono">+{formatCurrency(viewOrder.tax_amount, viewOrder.currency)}</span></div>}
              <div className="flex gap-8 font-bold text-base border-t pt-1"><span>Total:</span><span className="font-mono text-primary">{formatCurrency(viewOrder.total, viewOrder.currency)}</span></div>
            </>
          )}
        </div>

        {viewOrder.notes && <div className="text-xs text-muted-foreground p-2 bg-muted/20 rounded mt-2">{viewOrder.notes}</div>}

        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => printOrder(viewOrder)} data-testid="print-po-btn"><Printer className="w-4 h-4 mr-1" />Print</Button>
          
          {viewOrder.status === 'draft' && (
            <>
              <Button variant="outline" size="sm" onClick={() => { handleStatusAction(viewOrder.id, 'approve'); }}>
                <Check className="w-4 h-4 mr-1" />Approve
              </Button>
            </>
          )}
          
          {viewOrder.status === 'approved' && (
            <>
              <Button variant="outline" size="sm" onClick={() => handleSubmitToSupplier(viewOrder.id)}>
                <Send className="w-4 h-4 mr-1" />Submit to Supplier
              </Button>
              {user?.role === 'super_admin' && (
                <Button variant="outline" size="sm" onClick={() => handleRevertToDraft(viewOrder.id)} className="text-amber-600 border-amber-600/30 hover:bg-amber-500/10">
                  <Undo2 className="w-4 h-4 mr-1" />Revert to Draft
                </Button>
              )}
            </>
          )}
          
          {viewOrder.status === 'sent' && user?.role === 'super_admin' && (
            <Button variant="outline" size="sm" onClick={() => handleRevertToDraft(viewOrder.id)} className="text-amber-600 border-amber-600/30 hover:bg-amber-500/10">
              <Undo2 className="w-4 h-4 mr-1" />Revert to Draft
            </Button>
          )}
          
          {['submitted', 'partial_received'].includes(viewOrder.status) && (
            <Button variant="outline" size="sm" onClick={() => { setViewOrder(null); openReceiveDialog(viewOrder); }}>
              <Package className="w-4 h-4 mr-1" />Receive Goods
            </Button>
          )}
          
          {['submitted', 'partial_received', 'delivered'].includes(viewOrder.status) && (
            <Button variant="outline" size="sm" onClick={() => downloadDiscrepancyPDF(viewOrder.id)}>
              <FileDown className="w-4 h-4 mr-1" />Discrepancy Report
            </Button>
          )}
          
          {['approved', 'sent', 'received', 'delivered'].includes(viewOrder.status) && (
            <Button size="sm" onClick={() => { setPostingOrder(viewOrder); setShowPostDialog(true); setViewOrder(null); }} data-testid="post-po-btn">
              <ArrowRight className="w-4 h-4 mr-1" />Post as Invoice
            </Button>
          )}
          
          {/* Unpost and Delete for posted orders */}
          {viewOrder.status === 'posted' && (
            <>
              <Button variant="outline" size="sm" onClick={() => handleUnpost(viewOrder.id)} className="text-amber-600 border-amber-600/30 hover:bg-amber-500/10">
                <Undo2 className="w-4 h-4 mr-1" />Unpost
              </Button>
              <Button variant="destructive" size="sm" onClick={() => handleDelete(viewOrder.id, true)}>
                <Trash2 className="w-4 h-4 mr-1" />Delete
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  // ==================== POST DIALOG ====================
  const renderPostDialog = () => (
    <Dialog open={showPostDialog} onOpenChange={v => { if (!v) { setShowPostDialog(false); setPostingOrder(null); } }}>
      <DialogContent data-testid="post-po-dialog">
        <DialogHeader>
          <DialogTitle>Post as Purchase Invoice</DialogTitle>
          <DialogDescription>
            Convert {postingOrder?.order_number} to a purchase invoice. Select the accounting accounts.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Debit Account (Purchases/Inventory)</Label>
            <Select value={postDebitId} onValueChange={setPostDebitId}>
              <SelectTrigger data-testid="post-debit-select"><SelectValue placeholder="Select debit account" /></SelectTrigger>
              <SelectContent>
                {purchaseAccounts.map(a => (<SelectItem key={a.id} value={a.id}>{a.code} - {a.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Credit Account (Supplier)</Label>
            <Select value={postCreditId} onValueChange={setPostCreditId}>
              <SelectTrigger data-testid="post-credit-select"><SelectValue placeholder="Select supplier account" /></SelectTrigger>
              <SelectContent>
                {supplierAccounts.map(a => (<SelectItem key={a.id} value={a.id}>{a.code} - {a.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          {postingOrder && (
            <div className="p-3 bg-muted/20 rounded text-sm">
              <p>Order: <strong>{postingOrder.order_number}</strong></p>
              <p>Total: <strong className="text-primary">{formatCurrency(postingOrder.total, postingOrder.currency)}</strong></p>
              <p>Items: {postingOrder.lines.length}</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { setShowPostDialog(false); setPostingOrder(null); }}>Cancel</Button>
          <Button onClick={handlePost} disabled={posting} data-testid="confirm-post-btn">
            {posting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-1" />}
            Post as Invoice
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  // ==================== MAIN TABS VIEW ====================
  return (
    <div className="space-y-4" data-testid="purchase-orders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-primary" />
            Purchase Orders
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{filteredOrders.length} orders</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setActiveTab('analyze')} data-testid="analyze-btn">
            <BarChart3 className="w-4 h-4 mr-1" />Analyze Sales
          </Button>
          <Button onClick={() => { setEditingOrder(null); setOrder({ ...emptyOrder }); setShowForm(true); }} data-testid="new-po-btn">
            <Plus className="w-4 h-4 mr-1" />New Order
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">
            <LayoutDashboard className="w-4 h-4 mr-1" />Dashboard
          </TabsTrigger>
          <TabsTrigger value="list" data-testid="tab-list">
            <ClipboardList className="w-4 h-4 mr-1" />Orders
          </TabsTrigger>
          <TabsTrigger value="analyze" data-testid="tab-analyze">
            <BarChart3 className="w-4 h-4 mr-1" />Sales Analysis
          </TabsTrigger>
          <TabsTrigger value="supplier-analysis" data-testid="tab-supplier-analysis">
            <Truck className="w-4 h-4 mr-1" />Supplier Analysis
          </TabsTrigger>
        </TabsList>

        {/* DASHBOARD TAB */}
        <TabsContent value="dashboard" className="space-y-4">
          {loadingDashboard ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : dashboardData ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground">Pending Orders</p>
                        <p className="text-2xl font-bold text-blue-400">{dashboardData.summary.pending_orders}</p>
                      </div>
                      <div className="p-2 bg-blue-500/20 rounded-lg">
                        <ClipboardList className="w-5 h-5 text-blue-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-red-500/10 to-red-600/5 border-red-500/20">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground">Overdue Deliveries</p>
                        <p className="text-2xl font-bold text-red-400">{dashboardData.summary.overdue_deliveries}</p>
                      </div>
                      <div className="p-2 bg-red-500/20 rounded-lg">
                        <CalendarClock className="w-5 h-5 text-red-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border-emerald-500/20">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground">Received This Month</p>
                        <p className="text-2xl font-bold text-emerald-400">{dashboardData.summary.received_this_month}</p>
                      </div>
                      <div className="p-2 bg-emerald-500/20 rounded-lg">
                        <Package className="w-5 h-5 text-emerald-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/20">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground">Pending Value</p>
                        <div className="space-y-0.5">
                          {dashboardData.summary.pending_by_currency && Object.entries(dashboardData.summary.pending_by_currency).map(([currency, value]) => (
                            <p key={currency} className="text-lg font-bold text-amber-400">
                              {currency === 'EUR' ? 'EUR ' : 'FCFA '}{Number(value).toLocaleString()}
                            </p>
                          ))}
                          {(!dashboardData.summary.pending_by_currency || Object.keys(dashboardData.summary.pending_by_currency).length === 0) && (
                            <p className="text-2xl font-bold text-amber-400">0</p>
                          )}
                        </div>
                        {dashboardData.summary.avg_pending_discount > 0 && (
                          <p className="text-[10px] text-muted-foreground mt-1">
                            Avg discount: {dashboardData.summary.avg_pending_discount}%
                          </p>
                        )}
                      </div>
                      <div className="p-2 bg-amber-500/20 rounded-lg">
                        <DollarSign className="w-5 h-5 text-amber-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Monthly Spending Trend */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-primary" />
                      Monthly Spending (Last 6 Months)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {dashboardData.monthly_spending.map((month) => {
                        const monthName = new Date(month.month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
                        const currencies = month.by_currency || {};
                        const hasCurrencies = Object.keys(currencies).length > 0;
                        
                        return (
                          <div key={month.month} className="space-y-1">
                            <div className="flex justify-between text-xs">
                              <span className="text-muted-foreground">{monthName}</span>
                              <span className="font-mono text-muted-foreground">({month.order_count} orders)</span>
                            </div>
                            {hasCurrencies ? (
                              <div className="space-y-1">
                                {Object.entries(currencies).map(([currency, data]) => (
                                  <div key={currency} className="flex items-center justify-between text-xs pl-2 border-l-2 border-primary/30">
                                    <span className="font-medium">{currency}</span>
                                    <span className="font-mono">
                                      {currency === 'EUR' ? 'EUR ' : 'FCFA '}{Number(data.total).toLocaleString()}
                                      {data.avg_discount > 0 && (
                                        <span className="text-emerald-400 ml-1">({data.avg_discount}% disc)</span>
                                      )}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-xs text-muted-foreground pl-2">No spending</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {dashboardData.monthly_spending.length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-4">No spending data available</p>
                    )}
                  </CardContent>
                </Card>

                {/* Pending Orders by Supplier */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Truck className="w-4 h-4 text-primary" />
                      Pending Orders by Supplier
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {dashboardData.pending_by_supplier.length > 0 ? (
                      <div className="space-y-2 max-h-[280px] overflow-y-auto">
                        {dashboardData.pending_by_supplier.map((supplier, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 rounded-lg bg-muted/20 hover:bg-muted/30 transition-colors">
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-sm truncate">{supplier.supplier_name}</p>
                              <p className="text-xs text-muted-foreground">
                                {supplier.supplier_code && <span className="font-mono mr-2">{supplier.supplier_code}</span>}
                                Oldest: {formatDate(supplier.oldest_date)}
                              </p>
                            </div>
                            <div className="text-right ml-2">
                              <p className="font-mono text-sm font-medium text-primary">
                                {supplier.currency === 'EUR' ? 'EUR ' : 'FCFA '}{supplier.total_value.toLocaleString()}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {supplier.order_count} order{supplier.order_count !== 1 ? 's' : ''}
                                {supplier.total_discount > 0 && (
                                  <span className="text-emerald-400 ml-1">({supplier.total_discount}% disc)</span>
                                )}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">No pending orders</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Overdue Deliveries */}
              {dashboardData.overdue_deliveries.length > 0 && (
                <Card className="border-red-500/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2 text-red-400">
                      <AlertTriangle className="w-4 h-4" />
                      Overdue Deliveries ({dashboardData.overdue_deliveries.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto -mx-2 px-2">
                      <table className="w-full text-sm min-w-[700px]">
                        <thead>
                          <tr className="border-b bg-muted/10">
                            <th className="p-2 text-left text-xs">Order #</th>
                            <th className="p-2 text-left text-xs">Supplier</th>
                            <th className="p-2 text-left text-xs">Expected</th>
                            <th className="p-2 text-center text-xs">Days Overdue</th>
                            <th className="p-2 text-right text-xs">Value</th>
                            <th className="p-2 text-center text-xs">Status</th>
                            <th className="p-2 text-right text-xs">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dashboardData.overdue_deliveries.map((order) => (
                            <tr key={order.id} className="border-b hover:bg-muted/10">
                              <td className="p-2 font-mono text-xs">{order.order_number}</td>
                              <td className="p-2 truncate max-w-[150px]">{order.supplier_name || '-'}</td>
                              <td className="p-2 text-xs">{formatDate(order.expected_delivery_date)}</td>
                              <td className="p-2 text-center">
                                <Badge className="bg-red-500/20 text-red-400 text-xs">
                                  {order.days_overdue} days
                                </Badge>
                              </td>
                              <td className="p-2 text-right font-mono text-xs">
                                {order.currency === 'EUR' ? 'EUR ' : 'FCFA '}{(order.total || 0).toLocaleString()}
                                {order.discount_percent > 0 && (
                                  <span className="text-emerald-400 block text-[10px]">({order.discount_percent}% disc)</span>
                                )}
                              </td>
                              <td className="p-2 text-center">
                                <Badge className={statusColors[order.status] + ' text-[10px]'}>{statusLabels[order.status]}</Badge>
                              </td>
                              <td className="p-2 text-right">
                                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => {
                                  // Find the full order and open receive dialog
                                  const fullOrder = orders.find(o => o.id === order.id);
                                  if (fullOrder) {
                                    openReceiveDialog(fullOrder);
                                  } else {
                                    setActiveTab('list');
                                    setSearchTerm(order.order_number);
                                  }
                                }}>
                                  <Package className="w-3 h-3 mr-1" />Receive
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Status Breakdown */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Order Status Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(dashboardData.status_breakdown).map(([status, count]) => (
                      <div 
                        key={status} 
                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/20 cursor-pointer hover:bg-muted/30 transition-colors"
                        onClick={() => { setFilterStatus(status); setActiveTab('list'); }}
                      >
                        <Badge className={statusColors[status]}>{statusLabels[status] || status}</Badge>
                        <span className="font-mono font-bold">{count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Refresh Button */}
              <div className="flex justify-center">
                <Button variant="outline" size="sm" onClick={fetchDashboard} disabled={loadingDashboard}>
                  {loadingDashboard ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <BarChart3 className="w-4 h-4 mr-1" />}
                  Refresh Dashboard
                </Button>
              </div>
            </>
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <LayoutDashboard className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">Dashboard data not loaded</p>
                <Button onClick={fetchDashboard}>Load Dashboard</Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* LIST TAB */}
        <TabsContent value="list" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="p-3">
              <div className="flex flex-wrap gap-2 items-center">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search orders..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && fetchOrders()}
                    className="pl-8 h-9"
                    data-testid="search-orders-input"
                  />
                </div>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-[130px] h-9" data-testid="filter-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="submitted">Submitted</SelectItem>
                    <SelectItem value="partial_received">Partial</SelectItem>
                    <SelectItem value="delivered">Delivered</SelectItem>
                    <SelectItem value="posted">Posted</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="w-[140px] h-9" data-testid="filter-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="supplier">Supplier</SelectItem>
                    <SelectItem value="daily_sales">Daily Sales</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Desktop Table */}
          <div className="hidden md:block">
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="po-table">
                    <thead>
                      <tr className="border-b bg-muted/20">
                        <th className="p-3 text-left text-xs">Order #</th>
                        <th className="p-3 text-left text-xs">Date</th>
                        <th className="p-3 text-left text-xs">Type</th>
                        <th className="p-3 text-left text-xs">Supplier</th>
                        <th className="p-3 text-right text-xs">Items</th>
                        <th className="p-3 text-right text-xs">Total</th>
                        <th className="p-3 text-center text-xs">Status</th>
                        <th className="p-3 text-left text-xs">Invoice</th>
                        <th className="p-3 text-right text-xs">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr><td colSpan={9} className="p-8 text-center text-muted-foreground"><Loader2 className="w-5 h-5 mx-auto animate-spin" /></td></tr>
                      ) : filteredOrders.length === 0 ? (
                        <tr><td colSpan={9} className="p-8 text-center text-muted-foreground">No purchase orders found</td></tr>
                      ) : filteredOrders.map(o => (
                        <tr key={o.id} className="border-b hover:bg-muted/10 cursor-pointer" onClick={() => setViewOrder(o)}>
                          <td className="p-3 font-mono font-medium">{o.order_number}</td>
                          <td className="p-3">{formatDate(o.date)}</td>
                          <td className="p-3">
                            <Badge variant="outline" className="text-[10px]">
                              {o.order_type === 'daily_sales' ? 'Daily' : 'Supplier'}
                            </Badge>
                          </td>
                          <td className="p-3 truncate max-w-[180px]">{o.supplier_name || '-'}</td>
                          <td className="p-3 text-right">{o.lines?.length || 0}</td>
                          <td className="p-3 text-right font-mono text-xs">
                            {(() => {
                              const hasMixed = o.lines?.some(l => l.currency === 'XOF') && o.lines?.some(l => l.currency === 'EUR');
                              if (hasMixed) {
                                return <div><div>${(o.total_usd || 0).toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})}</div><div className="text-muted-foreground">EUR {(o.total_lbp || 0).toLocaleString()}</div></div>;
                              }
                              return formatCurrency(o.total || o.total_usd || 0, o.currency || 'EUR');
                            })()}
                          </td>
                          <td className="p-3 text-center"><Badge className={statusColors[o.status]}>{statusLabels[o.status]}</Badge></td>
                          <td className="p-3 text-xs">{o.purchase_invoice_number || '-'}</td>
                          <td className="p-3 text-right" onClick={e => e.stopPropagation()}>
                            <div className="flex items-center justify-end gap-1">
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setViewOrder(o)}><Eye className="w-3.5 h-3.5" /></Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => printOrder(o)}><Printer className="w-3.5 h-3.5" /></Button>
                              {o.status === 'draft' && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(o)}><Edit className="w-3.5 h-3.5" /></Button>}
                              {['submitted', 'partial_received'].includes(o.status) && (
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openReceiveDialog(o)}>
                                  <Package className="w-3.5 h-3.5 text-amber-500" />
                                </Button>
                              )}
                              {['approved', 'sent'].includes(o.status) && user?.role === 'super_admin' && (
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleRevertToDraft(o.id)} title="Revert to Draft">
                                  <Undo2 className="w-3.5 h-3.5 text-amber-500" />
                                </Button>
                              )}
                              {o.status !== 'posted' && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(o.id)}><Trash2 className="w-3.5 h-3.5 text-destructive" /></Button>}
                              {o.status === 'posted' && (
                                <>
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleUnpost(o.id)} title="Unpost"><Undo2 className="w-3.5 h-3.5 text-amber-500" /></Button>
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(o.id, true)} title="Force Delete"><Trash2 className="w-3.5 h-3.5 text-destructive" /></Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-2">
            {loading ? (
              <div className="p-8 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
            ) : filteredOrders.length === 0 ? (
              <Card><CardContent className="p-8 text-center text-muted-foreground">No purchase orders found</CardContent></Card>
            ) : filteredOrders.map(o => (
              <Card key={o.id} className="cursor-pointer hover:bg-muted/5 transition-colors" onClick={() => setViewOrder(o)}>
                <CardContent className="p-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-mono font-medium text-sm">{o.order_number}</p>
                      <p className="text-xs text-muted-foreground">{formatDate(o.date)}</p>
                    </div>
                    <Badge className={statusColors[o.status] + ' text-[10px]'}>{statusLabels[o.status]}</Badge>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <div className="text-xs text-muted-foreground">
                      {o.supplier_name || 'No supplier'} | {o.lines?.length || 0} items
                      <Badge variant="outline" className="text-[9px] ml-1">{o.order_type === 'daily_sales' ? 'Daily' : 'Supplier'}</Badge>
                    </div>
                    <span className="font-mono font-bold text-primary text-xs">
                      {o.currency === 'XOF' ? 'FCFA ' : ''}{(o.total || o.total_usd || 0).toLocaleString()}{o.currency === 'EUR' ? ' EUR' : (o.currency !== 'XOF' ? ' EUR' : '')}
                    </span>
                  </div>
                  {o.purchase_invoice_number && <p className="text-[10px] text-emerald-400 mt-1">Invoice: {o.purchase_invoice_number}</p>}
                  <div className="flex gap-1 mt-2 justify-end" onClick={e => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => printOrder(o)}><Printer className="w-3.5 h-3.5" /></Button>
                    {o.status === 'draft' && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(o)}><Edit className="w-3.5 h-3.5" /></Button>}
                    {['submitted', 'partial_received'].includes(o.status) && (
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openReceiveDialog(o)}>
                        <Package className="w-3.5 h-3.5 text-amber-500" />
                      </Button>
                    )}
                    {o.status !== 'posted' && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(o.id)}><Trash2 className="w-3.5 h-3.5 text-destructive" /></Button>}
                    {o.status === 'posted' && (
                      <>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleUnpost(o.id)} title="Unpost"><Undo2 className="w-3.5 h-3.5 text-amber-500" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(o.id, true)} title="Force Delete"><Trash2 className="w-3.5 h-3.5 text-destructive" /></Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* ANALYZE TAB */}
        <TabsContent value="analyze" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                Sales Analysis for Purchase Order
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">From Date</Label>
                  <DateInput value={analysisFromDate} onChange={setAnalysisFromDate} data-testid="analysis-from-date" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">To Date</Label>
                  <DateInput value={analysisToDate} onChange={setAnalysisToDate} data-testid="analysis-to-date" />
                </div>
                <div className="flex items-end">
                  <Button 
                    onClick={runAnalysis} 
                    disabled={analyzing} 
                    className="w-full"
                    data-testid="run-analysis-btn"
                  >
                    {analyzing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Calculator className="w-4 h-4 mr-1" />}
                    Analyze Sales
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Analyzes all items sold in the period. Shows qty sold, qty on hand, qty already on order, and suggested qty to order.
              </p>
            </CardContent>
          </Card>

          {/* Analysis Results */}
          {analysisResult && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between flex-wrap gap-2">
                  <span>Sold Items Analysis</span>
                  <Badge variant="outline">{analysisResult.days_in_period} days analyzed</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Summary Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                  <div className="p-3 bg-muted/20 rounded text-center">
                    <p className="text-xs text-muted-foreground">Items Sold</p>
                    <p className="text-xl font-bold">{analysisResult.total_items_sold}</p>
                  </div>
                  <div className="p-3 bg-muted/20 rounded text-center">
                    <p className="text-xs text-muted-foreground">Need Ordering</p>
                    <p className="text-xl font-bold text-primary">{analysisResult.items_to_reorder}</p>
                  </div>
                  <div className="p-3 bg-muted/20 rounded text-center">
                    <p className="text-xs text-muted-foreground">Selected</p>
                    <p className="text-xl font-bold text-emerald-500">
                      {Object.values(selectedSuggestions).filter(s => s.selected && filteredItemIds.has(s.item_id)).length}
                    </p>
                  </div>
                  <div className="p-3 bg-muted/20 rounded text-center">
                    <p className="text-xs text-muted-foreground">Est. Value (EUR)</p>
                    <p className="text-lg font-bold font-mono">
                      {Object.values(selectedSuggestions)
                        .filter(s => s.selected && filteredItemIds.has(s.item_id))
                        .reduce((sum, s) => sum + (s.qty * (s.unit_cost || 0)), 0).toLocaleString()} EUR
                    </p>
                  </div>
                </div>

                {/* Filter by Supplier */}
                {supplierAccounts.length > 0 && analysisResult?.suggestions?.length > 0 && (
                  <div className="flex items-center gap-2 mb-4 p-2 bg-muted/10 rounded">
                    <Filter className="w-4 h-4 text-muted-foreground" />
                    <Label className="text-xs">Filter by Supplier:</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-[320px] h-8 justify-between text-xs" data-testid="supplier-filter">
                          {supplierFilter === 'none'
                            ? `No Supplier (${analysisResult.suggestions.filter(x => !x.supplier_id).length})`
                            : supplierFilter
                              ? (() => {
                                  const s = supplierAccounts.find(a => a.id === supplierFilter);
                                  const count = analysisResult.suggestions.filter(x => x.supplier_id === supplierFilter).length;
                                  return s ? `${s.code} - ${s.name} (${count})` : 'Unknown';
                                })()
                              : `All Suppliers (${analysisResult.suggestions.length})`
                          }
                          <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[320px] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search suppliers..." className="h-8 text-xs" />
                          <CommandList>
                            <CommandEmpty>No supplier found.</CommandEmpty>
                            <CommandGroup>
                              <CommandItem onSelect={() => setSupplierFilter('')} className="text-xs">
                                <Check className={`mr-2 h-3 w-3 ${!supplierFilter ? 'opacity-100' : 'opacity-0'}`} />
                                All Suppliers ({analysisResult.suggestions.length})
                              </CommandItem>
                              <CommandItem onSelect={() => setSupplierFilter('none')} className="text-xs">
                                <Check className={`mr-2 h-3 w-3 ${supplierFilter === 'none' ? 'opacity-100' : 'opacity-0'}`} />
                                No Supplier ({analysisResult.suggestions.filter(x => !x.supplier_id).length})
                              </CommandItem>
                              {suppliersInResults.map(s => {
                                const count = analysisResult.suggestions.filter(x => x.supplier_id === s.id).length;
                                return (
                                  <CommandItem key={s.id} value={`${s.code} ${s.name}`} onSelect={() => setSupplierFilter(s.id)} className="text-xs">
                                    <Check className={`mr-2 h-3 w-3 ${supplierFilter === s.id ? 'opacity-100' : 'opacity-0'}`} />
                                    {s.code} - {s.name} ({count})
                                  </CommandItem>
                                );
                              })}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    {supplierFilter && (
                      <Button variant="ghost" size="sm" onClick={() => setSupplierFilter('')}>
                        <X className="w-3 h-3 mr-1" />Clear
                      </Button>
                    )}
                  </div>
                )}

                {filteredSuggestions.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    No sold items found in this period
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto -mx-2 px-2">
                      <table className="w-full text-xs min-w-[800px]" data-testid="suggestions-table">
                        <thead>
                          <tr className="border-b bg-muted/20">
                            <th className="p-2 w-8">
                              <input
                                type="checkbox"
                                checked={filteredSuggestions.every(s => selectedSuggestions[s.item_id]?.selected)}
                                onChange={e => {
                                  const checked = e.target.checked;
                                  setSelectedSuggestions(prev => {
                                    const updated = { ...prev };
                                    filteredSuggestions.forEach(s => {
                                      if (updated[s.item_id]) updated[s.item_id].selected = checked;
                                    });
                                    return updated;
                                  });
                                }}
                              />
                            </th>
                            <th className="p-2 text-left">Item</th>
                            <th className="p-2 text-left">Supplier</th>
                            <th className="p-2 text-right">Qty Sold</th>
                            <th className="p-2 text-right">On Hand</th>
                            <th className="p-2 text-right">On Order</th>
                            <th className="p-2 text-right">To Order</th>
                            <th className="p-2 text-right">Cost</th>
                            <th className="p-2 text-right">Disc%</th>
                            <th className="p-2 text-right">Line Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredSuggestions.map((item) => {
                            const sel = selectedSuggestions[item.item_id] || {};
                            const hasOnOrder = item.qty_on_order > 0;
                            return (
                              <tr key={item.item_id} className={`border-b hover:bg-muted/10 ${sel.selected ? '' : 'opacity-50'}`}>
                                <td className="p-2">
                                  <input
                                    type="checkbox"
                                    checked={sel.selected || false}
                                    onChange={() => toggleSuggestion(item.item_id)}
                                  />
                                </td>
                                <td className="p-2">
                                  <div>
                                    <p className="font-medium">{item.item_name}</p>
                                    <p className="text-[10px] text-muted-foreground">{item.barcode || item.item_code}</p>
                                  </div>
                                </td>
                                <td className="p-2">
                                  <span className="text-[10px]">{item.supplier_name || '-'}</span>
                                </td>
                                <td className="p-2 text-right font-mono font-medium">{item.qty_sold}</td>
                                <td className="p-2 text-right font-mono">{item.qty_on_hand}</td>
                                <td className="p-2 text-right">
                                  {hasOnOrder ? (
                                    <Badge className="bg-amber-500/20 text-amber-400 text-[10px]">
                                      {item.qty_on_order} pending
                                    </Badge>
                                  ) : (
                                    <span className="text-muted-foreground">0</span>
                                  )}
                                </td>
                                <td className="p-2">
                                  <Input
                                    type="number"
                                    className="h-7 w-20 text-right"
                                    value={sel.qty ?? 0}
                                    onChange={e => updateSuggestionQty(item.item_id, e.target.value)}
                                    min="0"
                                  />
                                </td>
                                <td className="p-2 text-right font-mono text-[10px]">
                                  {item.currency === 'XOF' ? 'FCFA ' : ''}{item.unit_cost?.toLocaleString()}{item.currency === 'EUR' ? ' EUR' : ''}
                                </td>
                                <td className="p-2 text-right font-mono text-[10px]">
                                  {item.discount_percent || 0}%
                                </td>
                                <td className="p-2 text-right font-mono font-medium text-[10px]">
                                  {item.currency === 'XOF' ? 'FCFA ' : ''}{((sel.qty ?? 0) * (item.unit_cost || 0)).toLocaleString()}{item.currency === 'EUR' ? ' EUR' : ''}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    <div className="flex justify-between items-center gap-2 mt-4 pt-4 border-t">
                      <div className="text-sm text-muted-foreground">
                        Showing {filteredSuggestions.length} of {analysisResult.suggestions.length} items
                      </div>
                      <Button
                        onClick={createPOFromSuggestions}
                        disabled={!Object.values(selectedSuggestions).some(s => s.selected && s.qty > 0)}
                        data-testid="create-po-from-analysis-btn"
                      >
                        <ClipboardList className="w-4 h-4 mr-1" />
                        Create Purchase Order
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* SUPPLIER ANALYSIS TAB */}
        <TabsContent value="supplier-analysis" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="w-5 h-5 text-primary" />
                Supplier Analysis for Purchase Order
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs">Supplier</Label>
                  <SearchableSupplierSelect
                    suppliers={supplierAccounts}
                    value={saSupplier}
                    onValueChange={setSaSupplier}
                    placeholder="Select supplier..."
                    testId="sa-supplier-select"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">From Date</Label>
                  <DateInput value={saFromDate} onChange={setSaFromDate} data-testid="sa-from-date" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">To Date</Label>
                  <DateInput value={saToDate} onChange={setSaToDate} data-testid="sa-to-date" />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Button onClick={runSupplierAnalysis} disabled={saAnalyzing || !saSupplier} data-testid="run-supplier-analysis-btn">
                  {saAnalyzing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Calculator className="w-4 h-4 mr-1" />}
                  Analyze Supplier
                </Button>
                <p className="text-xs text-muted-foreground">
                  Shows all items from selected supplier with sales + purchase data for the period.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Supplier Analysis Results */}
          {saResult && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
                <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
                  <CardContent className="p-3 text-center">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Total Items</p>
                    <p className="text-2xl font-bold text-blue-400">{saResult.total_items}</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border-emerald-500/20">
                  <CardContent className="p-3 text-center">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider">With Sales</p>
                    <p className="text-2xl font-bold text-emerald-400">{saResult.items_with_sales}</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/20">
                  <CardContent className="p-3 text-center">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider">No Sales</p>
                    <p className="text-2xl font-bold text-amber-400">{saResult.items_no_sales}</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-red-500/10 to-red-600/5 border-red-500/20">
                  <CardContent className="p-3 text-center">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Dead Stock</p>
                    <p className="text-2xl font-bold text-red-400">{saResult.dead_stock_count}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider">On-Time Rate</p>
                    <p className="text-2xl font-bold">{saResult.supplier_performance?.on_time_rate || 0}%</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Fill Rate</p>
                    <p className="text-2xl font-bold">{saResult.supplier_performance?.fill_rate || 0}%</p>
                  </CardContent>
                </Card>
              </div>

              {/* AI Insights Section */}
              <Card className="border-purple-500/30 bg-gradient-to-br from-purple-500/5 to-transparent">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-purple-400" />
                      AI Insights — Claude Opus
                    </span>
                    <div className="flex items-center gap-2">
                      <Select value={saAiLang} onValueChange={setSaAiLang}>
                        <SelectTrigger className="h-8 w-[100px] text-xs border-purple-500/30" data-testid="ai-lang-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="en">English</SelectItem>
                          <SelectItem value="fr">Français</SelectItem>
                          <SelectItem value="ar">العربية</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={fetchAiInsights}
                        disabled={saAiLoading}
                        className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                        data-testid="fetch-ai-insights-btn"
                      >
                        {saAiLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                        {saAiInsights ? 'Refresh AI' : 'Generate Insights'}
                      </Button>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {saAiLoading ? (
                    <div className="flex items-center gap-3 py-6 justify-center text-muted-foreground">
                      <Loader2 className="w-5 h-5 animate-spin text-purple-400" />
                      <span className="text-sm">Claude is analyzing supplier data...</span>
                    </div>
                  ) : saAiInsights ? (
                    <div className="space-y-4">
                      {/* Supplier Note */}
                      {saAiInsights.supplier_note && (
                        <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                          <p className="text-xs font-semibold text-blue-400 uppercase tracking-wider mb-1">Supplier Assessment</p>
                          <p className="text-sm">{saAiInsights.supplier_note}</p>
                        </div>
                      )}
                      {/* Reorder Advice */}
                      {saAiInsights.reorder_advice && (
                        <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                          <p className="text-xs font-semibold text-emerald-400 uppercase tracking-wider mb-1">Smart Reorder Strategy</p>
                          <p className="text-sm">{saAiInsights.reorder_advice}</p>
                        </div>
                      )}
                      {/* Key Insight */}
                      {saAiInsights.key_insight && (
                        <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                          <p className="text-xs font-semibold text-amber-400 uppercase tracking-wider mb-1">Key Insight</p>
                          <p className="text-sm">{saAiInsights.key_insight}</p>
                        </div>
                      )}
                      {/* Dead Stock Actions */}
                      {saAiInsights.dead_stock_actions && saAiInsights.dead_stock_actions.length > 0 && (
                        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                          <p className="text-xs font-semibold text-red-400 uppercase tracking-wider mb-2">Dead Stock Actions</p>
                          <div className="space-y-1.5">
                            {saAiInsights.dead_stock_actions.map((action, idx) => (
                              <div key={idx} className="flex items-start gap-2 text-xs">
                                <Badge className={
                                  action.action === 'liquidate' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                                  action.action === 'discount' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' :
                                  action.action === 'return' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
                                  'bg-slate-500/20 text-slate-400 border-slate-500/30'
                                }>{action.action}</Badge>
                                <span className="font-medium">{action.item_name}</span>
                                <span className="text-muted-foreground flex-1">— {action.reason}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      Click "Generate Insights" for AI-powered analysis of this supplier's performance, smart reorder suggestions, and dead stock recommendations.
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Items Table */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span>{saResult.supplier_name} — Items ({saResult.total_items})</span>
                    <Badge variant="outline">{saResult.days_in_period} days analyzed</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {saResult.items.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground">No items found for this supplier</div>
                  ) : (
                    <>
                      {/* Summary Stats */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                        <div className="p-3 bg-muted/20 rounded text-center">
                          <p className="text-xs text-muted-foreground">Selected</p>
                          <p className="text-xl font-bold text-emerald-500">
                            {Object.values(saSelected).filter(s => s.selected).length}
                          </p>
                        </div>
                        <div className="p-3 bg-muted/20 rounded text-center">
                          <p className="text-xs text-muted-foreground">Selected Items</p>
                          <p className="text-xl font-bold text-primary">
                            {Object.values(saSelected).filter(s => s.selected && s.qty > 0).length}
                          </p>
                        </div>
                        <div className="p-3 bg-muted/20 rounded text-center">
                          <p className="text-xs text-muted-foreground">Est. Order Value</p>
                          <p className="text-lg font-bold font-mono">
                            {Object.values(saSelected)
                              .filter(s => s.selected)
                              .reduce((sum, s) => sum + (s.qty * (s.unit_cost || 0)), 0).toLocaleString()} EUR
                          </p>
                        </div>
                        <div className="p-3 bg-muted/20 rounded text-center">
                          <p className="text-xs text-muted-foreground">Avg Delay</p>
                          <p className="text-xl font-bold">{saResult.supplier_performance?.avg_delay_days || 0}d</p>
                        </div>
                      </div>

                      <div className="overflow-x-auto -mx-2 px-2">
                        <table className="w-full text-xs min-w-[900px]" data-testid="sa-items-table">
                          <thead>
                            <tr className="border-b bg-muted/20">
                              <th className="p-2 w-8">
                                <input
                                  type="checkbox"
                                  checked={saResult.items.every(i => saSelected[i.item_id]?.selected)}
                                  onChange={e => {
                                    const checked = e.target.checked;
                                    setSaSelected(prev => {
                                      const updated = { ...prev };
                                      saResult.items.forEach(i => {
                                        if (updated[i.item_id]) updated[i.item_id].selected = checked;
                                      });
                                      return updated;
                                    });
                                  }}
                                />
                              </th>
                              <th className="p-2 text-left">Item</th>
                              <th className="p-2 text-right">Qty Sold</th>
                              <th className="p-2 text-right">Qty Purchased</th>
                              <th className="p-2 text-right">On Hand</th>
                              <th className="p-2 text-right">On Order</th>
                              <th className="p-2 text-right">To Order</th>
                              <th className="p-2 text-right">Cost</th>
                              <th className="p-2 text-right">Sell</th>
                              <th className="p-2 text-right">Margin</th>
                              <th className="p-2 text-right">Line Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {saResult.items.map((item) => {
                              const sel = saSelected[item.item_id] || {};
                              return (
                                <tr key={item.item_id} className={`border-b hover:bg-muted/10 ${sel.selected ? '' : 'opacity-50'} ${item.is_dead_stock ? 'bg-red-500/5' : ''}`}>
                                  <td className="p-2">
                                    <input type="checkbox" checked={sel.selected || false}
                                      onChange={() => setSaSelected(prev => ({
                                        ...prev, [item.item_id]: { ...prev[item.item_id], selected: !prev[item.item_id]?.selected }
                                      }))}
                                    />
                                  </td>
                                  <td className="p-2">
                                    <div>
                                      <p className="font-medium">{item.item_name}</p>
                                      <p className="text-[10px] text-muted-foreground">
                                        {item.barcode || item.item_code}
                                        {item.is_dead_stock && <Badge className="ml-1 bg-red-500/20 text-red-400 text-[8px] px-1">DEAD STOCK</Badge>}
                                      </p>
                                    </div>
                                  </td>
                                  <td className="p-2 text-right font-mono font-medium">
                                    {item.qty_sold > 0 ? item.qty_sold : <span className="text-muted-foreground">0</span>}
                                  </td>
                                  <td className="p-2 text-right font-mono">
                                    {item.qty_purchased > 0 ? item.qty_purchased : <span className="text-muted-foreground">0</span>}
                                  </td>
                                  <td className="p-2 text-right font-mono">{item.qty_on_hand}</td>
                                  <td className="p-2 text-right">
                                    {item.qty_on_order > 0 ? (
                                      <Badge className="bg-amber-500/20 text-amber-400 text-[10px]">{item.qty_on_order}</Badge>
                                    ) : <span className="text-muted-foreground">0</span>}
                                  </td>
                                  <td className="p-2">
                                    <Input type="number" className="h-7 w-20 text-right text-xs"
                                      value={sel.qty ?? 0}
                                      onChange={e => setSaSelected(prev => ({
                                        ...prev, [item.item_id]: { ...prev[item.item_id], qty: parseInt(e.target.value) || 0 }
                                      }))}
                                      min="0"
                                    />
                                  </td>
                                  <td className="p-2 text-right font-mono text-[10px]">
                                    {item.currency === 'XOF' ? 'FCFA ' : ''}{item.unit_cost?.toLocaleString()}{item.currency === 'EUR' ? ' €' : ''}
                                  </td>
                                  <td className="p-2 text-right font-mono text-[10px]">
                                    {item.currency === 'XOF' ? 'FCFA ' : ''}{item.selling_price?.toLocaleString()}{item.currency === 'EUR' ? ' €' : ''}
                                  </td>
                                  <td className="p-2 text-right text-[10px]">
                                    <span className={item.margin_percent > 20 ? 'text-emerald-400' : item.margin_percent > 0 ? 'text-amber-400' : 'text-red-400'}>
                                      {item.margin_percent}%
                                    </span>
                                  </td>
                                  <td className="p-2 text-right font-mono font-medium text-[10px]">
                                    {item.currency === 'XOF' ? 'FCFA ' : ''}{((sel.qty ?? 0) * (item.unit_cost || 0)).toLocaleString()}{item.currency === 'EUR' ? ' €' : ''}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      <div className="flex justify-between items-center gap-2 mt-4 pt-4 border-t">
                        <div className="text-sm text-muted-foreground">
                          {saResult.items.length} items from {saResult.supplier_name}
                        </div>
                        <Button
                          onClick={createPOFromSupplierAnalysis}
                          disabled={!Object.values(saSelected).some(s => s.selected && s.qty > 0)}
                          data-testid="create-po-from-supplier-btn"
                        >
                          <ClipboardList className="w-4 h-4 mr-1" />
                          Create Purchase Order
                        </Button>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>

      {renderViewDialog()}
      {renderPostDialog()}
    </div>
  );
};

export default PurchaseOrdersPage;
